import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, API } from "@/lib/api";
import { submitPayment } from "@/lib/payment";
import { useAuth } from "@/context/AuthContext";
import { AuthDialog } from "@/components/AuthDialog";
import { LivingOrderDialog } from "@/components/LivingOrderDialog";
import { NATIONALITIES } from "@/lib/nationalities";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Loader2, Flame, ImagePlus, Trash2, ShieldCheck, Send, Lock, X,
  ChevronLeft, ChevronRight, Video, Copy, Mail, Gift, Link2, Plus, Pencil, Users, Music, Heart, Check, Play, Sparkles,
  Volume2, VolumeX, Eye, FileText, Download, Paperclip,
} from "lucide-react";

const euros = (c) => `${((c || 0) / 100).toFixed((c % 100) ? 2 : 0)} €`;

const MUSIC_LIB = [
  { id: "default", label: "Douce", labelKey: "po.music_soft" },
  { id: "piano", label: "Piano", labelKey: "po.music_piano" },
  { id: "guitare", label: "Guitare", labelKey: "po.music_guitar" },
  { id: "violon", label: "Violon", labelKey: "po.music_violin" },
  { id: "saxo", label: "Saxophone", labelKey: "po.music_saxophone" },
  { id: "harpe", label: "Harpe", labelKey: "po.music_harp" },
  { id: "serein", label: "Sereine", labelKey: "po.music_serene" },
  { id: "tendresse", label: "Tendresse", labelKey: "po.music_tenderness" },
  { id: "postinity", label: "Postinity" },
];
const MUSIC_KEYS = { default: "po.music_soft", piano: "po.music_piano", guitare: "po.music_guitar", violon: "po.music_violin", saxo: "po.music_saxophone", harpe: "po.music_harp", serein: "po.music_serene", tendresse: "po.music_tenderness" };
const MUSIC_FR = { default: "Douce", piano: "Piano", guitare: "Guitare", violon: "Violon", saxo: "Saxophone", harpe: "Harpe", serein: "Sereine", tendresse: "Tendresse" };
const musicLabel = (t, m) => {
  const key = MUSIC_KEYS[m.id];
  if (key && (!m.label || m.label === MUSIC_FR[m.id])) return t(key);
  return m.label || (key ? t(key) : m.id);
};
const MUSIC_FILE = {
  default: "/diaporama-default.mp3",
  piano: "/diaporama-piano.mp3",
  guitare: "/diaporama-guitare.mp3",
  violon: "/diaporama-violon.mp3",
  saxo: "/diaporama-saxo.mp3",
  harpe: "/diaporama-harpe.mp3",
  serein: "/diaporama-serein.mp3",
  tendresse: "/diaporama-tendresse.mp3",
  postinity: "/diaporama-postinity.mp3",
};
// Résout la source audio d'un id de piste à partir de la bibliothèque dynamique (admin)
const MEDIA_BASE = (API || "").replace(/\/api$/, "");
const trackFileSrc = (track) => track.custom ? `${MEDIA_BASE}${track.file}` : `${process.env.PUBLIC_URL}${track.file}`;
const resolveMusicSrc = (tracks, choice, ownReady, ownUrl) => {
  if (choice === "own") return ownReady && ownUrl ? ownUrl : `${process.env.PUBLIC_URL}${MUSIC_FILE.default}`;
  const t = (tracks || []).find((x) => x.id === choice);
  if (t) return trackFileSrc(t);
  return `${process.env.PUBLIC_URL}${MUSIC_FILE[choice] || MUSIC_FILE.default}`;
};

const MAX_PHOTOS = 30;
const MAX_VIDEO_SEC = 120; // 2 minutes
// Contraintes caméra haute qualité (diffusion TV / rétroprojecteur)
const HQ_VIDEO_CONSTRAINTS = { facingMode: "user", width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30 } };
const recorderOptions = (stream, mime) => {
  const h = stream?.getVideoTracks?.()[0]?.getSettings?.().height || 720;
  const videoBitsPerSecond = h >= 1080 ? 6000000 : h >= 720 ? 4000000 : 2500000;
  const opts = { videoBitsPerSecond, audioBitsPerSecond: 128000 };
  if (mime) opts.mimeType = mime;
  return opts;
};
const fmtSec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const ACCEPT_DOCS = ".pdf,.doc,.docx,.txt,.odt,.rtf,.ppt,.pptx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain";
const fmtSize = (b) => { b = b || 0; if (b < 1024) return `${b} o`; if (b < 1048576) return `${(b / 1024).toFixed(0)} Ko`; return `${(b / 1048576).toFixed(1)} Mo`; };
// askDelete est défini dans le composant (utilise i18n)

// 3 lignes de garants toujours affichées, éditables à tout moment
function padGarantes(list) {
  const a = (list || []).map((g) => ({ name: g.name || "", email: g.email || "", code: g.code }));
  while (a.length < 3) a.push({ name: "", email: "" });
  return a.slice(0, 3);
}

// Chemin de fer : étapes numérotées — VERT si rempli, ROUGE s'il manque une info
function StepRail({ steps }) {
  const { t } = useTranslation();
  const allDone = steps.every((s) => s.done);
  return (
    <div className="mb-4" data-testid="step-rail">
      <div className="flex items-start">
        {steps.map((s, i) => (
          <div key={i} className={`flex items-start ${i < steps.length - 1 ? "flex-1" : ""}`}>
            <div className="flex w-14 shrink-0 flex-col items-center">
              <div className={`flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors duration-500 ${s.done ? "border-emerald-400 bg-emerald-500 text-black" : "border-amber-400/50 bg-amber-400/10 text-amber-300"}`} data-testid={`step-${s.n}${s.done ? "-done" : ""}`}>
                {s.done ? <Check size={16} /> : s.n}
              </div>
              <span className={`mt-1.5 text-center text-[10px] leading-tight transition-colors duration-500 ${s.done ? "text-emerald-200" : "text-amber-200/80"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`mt-4 h-0.5 flex-1 rounded transition-colors duration-500 ${s.done ? "bg-emerald-400" : "bg-amber-400/25"}`} />
            )}
          </div>
        ))}
      </div>
      <div className={`mt-2 flex items-center justify-center gap-1.5 text-[11px] font-medium ${allDone ? "text-emerald-300" : "text-amber-200/80"}`} data-testid="step-rail-status">
        {allDone ? <><Check size={12} /> {t("po.step_alldone")}</> : <>{t("po.step_missing")}</>}
      </div>
    </div>
  );
}

// Onglet numéroté : VERT si rempli/validé, ROUGE si vide
function LcTab({ value, n, label, Icon, done }) {
  const { t } = useTranslation();
  return (
    <TabsTrigger
      value={value}
      data-testid={`lc-tab-${value}`}
      className={`flex h-auto flex-col items-center gap-1 rounded-xl border-2 px-1 py-2 text-[11px] font-semibold leading-tight shadow-lg backdrop-blur-md transition-colors data-[state=active]:ring-2 data-[state=active]:ring-white/60 sm:text-xs ${done ? "border-emerald-400 bg-slate-900/60 text-emerald-200 data-[state=active]:bg-emerald-600/55" : "border-amber-400/70 bg-slate-900/60 text-amber-100 data-[state=active]:bg-amber-600/45"}`}
    >
      <span className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold transition-colors ${done ? "bg-emerald-500 text-black" : "bg-amber-400 text-black"}`}>
        {done ? <Check size={15} /> : n}
      </span>
      <span className="flex items-center gap-1 text-center"><Icon size={13} className="hidden sm:inline" /> {label}</span>
      <span className="rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[8px] font-bold text-emerald-300 sm:text-[9px]">{t("po.free_badge")}</span>
    </TabsTrigger>
  );
}

// Bouton « Valider et passer à l'étape suivante » : au clic, l'onglet passe en vert et on avance
function ValidateStepButton({ validated, onClick, isLast, testid }) {
  const { t } = useTranslation();
  const label = validated
    ? (isLast ? t("po.vs_done_last") : t("po.vs_done"))
    : (isLast ? t("po.vs_last") : t("po.vs_next"));
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`mt-6 flex w-full items-center justify-center gap-2 rounded-full border-2 py-3 text-sm font-semibold transition-colors ${
        validated
          ? "border-emerald-400 bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30"
          : "border-amber-400 bg-amber-500/90 text-black hover:bg-amber-400"
      }`}
    >
      <Check size={16} /> {label} {!isLast && <ChevronRight size={16} />}
    </button>
  );
}

// Puce numérotée d'un sous-titre de section : VERTE (coche) si le champ est rempli
function StepNum({ n, done, tone = "amber" }) {
  const base = "flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold transition-colors";
  if (done) return <span className={`${base} bg-emerald-500 text-black`}><Check size={12} /></span>;
  const tones = { amber: "bg-amber-500/20 text-amber-300", sky: "bg-sky-500/20 text-sky-300" };
  return <span className={`${base} ${tones[tone]}`}>{n}</span>;
}

// Petit sélecteur OUI / NON (par défaut OUI). NON masque la section.
function WantToggle({ value, onChange, testid }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex overflow-hidden rounded-full border border-white/20 text-[11px] font-semibold" data-testid={testid}>
      <button type="button" onClick={() => onChange(true)} data-testid={testid ? `${testid}-yes` : undefined}
        className={`px-2.5 py-0.5 transition-colors ${value ? "bg-emerald-500 text-black" : "text-white/70 hover:bg-white/10"}`}>{t("po.yes")}</button>
      <button type="button" onClick={() => onChange(false)} data-testid={testid ? `${testid}-no` : undefined}
        className={`px-2.5 py-0.5 transition-colors ${!value ? "bg-white/80 text-black" : "text-white/70 hover:bg-white/10"}`}>{t("po.no")}</button>
    </span>
  );
}


// Sélecteur de musique du diaporama (bibliothèque + import + suppression)
function MusicPicker({ tracks, on, choice, ownReady, ownName, ownUrl, busy, progress, onToggle, onChoose, onImport, onRemove, inputRef, onFile, prefix, onSave }) {
  const { t } = useTranslation();
  const lib = (tracks && tracks.length) ? tracks : MUSIC_LIB.map((m) => ({ ...m, custom: false, file: MUSIC_FILE[m.id] }));
  const previewSrc = resolveMusicSrc(lib, choice, ownReady, ownUrl);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`${prefix}-music-section`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className={`flex items-center gap-1.5 text-sm font-medium ${on ? "text-emerald-300" : "text-amber-300"}`}><Music size={16} /> {t("po.music_title")}</span>
        <button type="button" onClick={onToggle} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-200" : "border-white/20 bg-white/5 text-muted-foreground"}`} data-testid={`${prefix}-music-toggle-btn`}>
          {on ? <Volume2 size={13} /> : <VolumeX size={13} />} {on ? t("po.music_on") : t("po.music_off")}
        </button>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">{t("po.music_help")}</p>
      <div className="mb-3 flex flex-wrap gap-2" data-testid={`${prefix}-music-chips`}>
        {lib.map((m) => (
          <button key={m.id} type="button" onClick={() => onChoose(m.id)} data-testid={`${prefix}-music-choice-${m.id}`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${choice === m.id ? "border-emerald-400 bg-emerald-500/20 text-emerald-100" : "border-white/15 bg-white/5 text-white/80 hover:bg-white/10"}`}>
            {choice === m.id ? "✓ " : ""}{musicLabel(t, m)}
          </button>
        ))}
        {ownReady && (
          <button type="button" onClick={() => onChoose("own")} data-testid={`${prefix}-music-choice-own`}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${choice === "own" ? "border-emerald-400 bg-emerald-500/20 text-emerald-100" : "border-white/15 bg-white/5 text-white/80 hover:bg-white/10"}`}>
            {choice === "own" ? "✓ " : ""}🎵 {t("po.my_music")}
          </button>
        )}
      </div>
      <audio key={previewSrc} src={previewSrc} controls className="mb-3 h-9 w-full" data-testid={`${prefix}-music-preview`} />
      {onSave && (
        <div className="mb-3 flex justify-center">
          <Button size="sm" className="rounded-full bg-sky-500 text-black hover:bg-sky-400" onClick={onSave} data-testid={`${prefix}-music-save-btn`}><Check size={14} className="mr-1.5" /> {t("po.save_choice")}</Button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" className="rounded-full border-sky-400/40 text-sky-200 hover:bg-sky-500/10" onClick={onImport} disabled={busy} data-testid={`${prefix}-music-upload-btn`}>
          {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Music size={13} className="mr-1.5" />} {busy ? `${t("po.sending")} ${progress}%` : (ownReady ? t("po.replace_music") : t("po.import_music"))}
        </Button>
        {ownReady && (
          <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={onRemove} data-testid={`${prefix}-music-remove-btn`}><Trash2 size={13} className="mr-1.5" /> {t("po.delete_music")}</Button>
        )}
        {ownReady && <span className="text-[11px] text-muted-foreground truncate max-w-[160px]">{ownName || t("po.music_file_default")}</span>}
      </div>
      {busy && <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full bg-sky-400 transition-all" style={{ width: `${progress}%` }} /></div>}
      <input ref={inputRef} type="file" accept=".mp3,.mp4,.m4a,audio/*,video/mp4" className="hidden" onChange={onFile} data-testid={`${prefix}-music-input`} />
    </div>
  );
}

async function fileToCompressedDataUrl(file, maxDim = 1400, quality = 0.82) {
  const dataUrl = await new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = rej;
    fr.readAsDataURL(file);
  });
  return await new Promise((res) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const r = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * r); height = Math.round(height * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      res(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => res(dataUrl);
    img.src = dataUrl;
  });
}

export default function LivingCandle() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const askDelete = (whatKey = "del_item") => window.confirm(t("po.del_confirm", { what: t(`po.${whatKey}`) }));
  const [lc, setLc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [citation, setCitation] = useState("");
  const [photos, setPhotos] = useState([]);
  const [mainPhoto, setMainPhoto] = useState(null);
  const [birthDate, setBirthDate] = useState("");
  const [deathDate, setDeathDate] = useState("");
  const [nationality, setNationality] = useState("");
  const mainPhotoRef = useRef();
  const [garantes, setGarantes] = useState(padGarantes([]));
  const [slide, setSlide] = useState(0);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [activeTab, setActiveTab] = useState("ceremonie");
  const [cerSlide, setCerSlide] = useState(0);
  const [showCerSlideshow, setShowCerSlideshow] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [validatedSteps, setValidatedSteps] = useState([]);
  const [showGarantPreview, setShowGarantPreview] = useState(false);
  const [musicTracks, setMusicTracks] = useState([]);
  useEffect(() => {
    api.get("/public/diaporama-music").then((r) => setMusicTracks(r.data?.tracks || [])).catch(() => {});
  }, []);
  const [musicOn, setMusicOn] = useState(true);
  const [musicChoice, setMusicChoice] = useState("default");
  const [musicName, setMusicName] = useState(null);
  const [musicReady, setMusicReady] = useState(false);
  const [musicBusy, setMusicBusy] = useState(false);
  const [musicProgress, setMusicProgress] = useState(0);
  const [musicUrl, setMusicUrl] = useState(null);
  const [cerMusicOn, setCerMusicOn] = useState(true);
  const [cerMusicChoice, setCerMusicChoice] = useState("default");
  const [cerMusicName, setCerMusicName] = useState(null);
  const [cerMusicReady, setCerMusicReady] = useState(false);
  const [cerMusicBusy, setCerMusicBusy] = useState(false);
  const [cerMusicProgress, setCerMusicProgress] = useState(0);
  const [cerMusicUrl, setCerMusicUrl] = useState(null);
  const musicInputRef = useRef();
  const cerMusicInputRef = useRef();
  const slideAudioRef = useRef(null);
  const fileRef = useRef();
  const videoInputRef = useRef();

  // ---- Phase 2 : dossier de cérémonie + messages individuels ----
  const [ceremony, setCeremony] = useState({ enabled: false, payer: "owner", owner_paid: false, text: "", songs: [], photos: [], video_ready: false, files: [], want_text: true, want_photos: true, want_video: true, want_file: true, access_code: null });
  const ceremonyRef = useRef({});
  useEffect(() => { ceremonyRef.current = ceremony; }, [ceremony]);
  const [songInput, setSongInput] = useState("");
  const [cerVideoBusy, setCerVideoBusy] = useState(false);
  const [cerVideoProgress, setCerVideoProgress] = useState(0);
  const [cerDocBusy, setCerDocBusy] = useState(false);
  const [msgDocBusy, setMsgDocBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const [confirmDelVideo, setConfirmDelVideo] = useState(false);
  const [cerVideoUrl, setCerVideoUrl] = useState(null);
  const [msgVideoUrl, setMsgVideoUrl] = useState(null);
  const [msgOwnMusicUrl, setMsgOwnMusicUrl] = useState(null);
  const [msgMusicBusy, setMsgMusicBusy] = useState(false);
  const [msgMusicProgress, setMsgMusicProgress] = useState(0);
  const msgMusicRef = useRef();
  const [recLeft, setRecLeft] = useState(120);
  const mediaRecRef = useRef(null);
  const recStreamRef = useRef(null);
  const recChunksRef = useRef([]);
  const recTimerRef = useRef(null);
  const recPreviewRef = useRef(null);
  const cerPhotoRef = useRef();
  const cerVideoRef = useRef();
  const cerDocRef = useRef();
  const msgDocRef = useRef();
  const [pricing, setPricing] = useState({ living_ceremony_price_cents: 2900, living_message_price_cents: 500, living_access_type: "download", living_access_days: 0 });
  const [messages, setMessages] = useState([]);
  const [msgOpen, setMsgOpen] = useState(false);
  const [credits, setCredits] = useState(0);
  const [prepaidCeremony, setPrepaidCeremony] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [em, setEm] = useState(null); // message being edited (form state)
  const [msgPreview, setMsgPreview] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("register");
  const [pvSlide, setPvSlide] = useState(0);
  useEffect(() => {
    if (!msgPreview || !em || (em.photos || []).length < 2) return;
    const t = setInterval(() => setPvSlide((s) => (s + 1) % (em.photos || []).length), 3500);
    return () => clearInterval(t);
  }, [msgPreview, em]);

  useEffect(() => {
    let revoked = null;
    if (ceremony.video_ready) {
      api.get("/living-candle/ceremony/video", { responseType: "blob" })
        .then((r) => { const u = URL.createObjectURL(r.data); revoked = u; setCerVideoUrl(u); })
        .catch(() => setCerVideoUrl(null));
    } else {
      setCerVideoUrl(null);
    }
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [ceremony.video_ready]);

  useEffect(() => {
    let revoked = null;
    if (msgOpen && em?.id && em?.video_ready) {
      api.get(`/living-candle/messages/${em.id}/video`, { responseType: "blob" })
        .then((r) => { const u = URL.createObjectURL(r.data); revoked = u; setMsgVideoUrl(u); })
        .catch(() => setMsgVideoUrl(null));
    } else {
      setMsgVideoUrl(null);
    }
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [msgOpen, em?.id, em?.video_ready]);

  useEffect(() => {
    let revoked = null;
    if (msgOpen && em?.id && em?.music_ready) {
      api.get(`/living-candle/messages/${em.id}/music`, { responseType: "blob" })
        .then((r) => { const u = URL.createObjectURL(r.data); revoked = u; setMsgOwnMusicUrl(u); })
        .catch(() => setMsgOwnMusicUrl(null));
    } else {
      setMsgOwnMusicUrl(null);
    }
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [msgOpen, em?.id, em?.music_ready]);
  const [msgBusy, setMsgBusy] = useState(false);
  const [msgVideoBusy, setMsgVideoBusy] = useState(false);
  const [msgVideoProgress, setMsgVideoProgress] = useState(0);
  const [msgCamOpen, setMsgCamOpen] = useState(false);
  const [msgRecording, setMsgRecording] = useState(false);
  const [msgRecLeft, setMsgRecLeft] = useState(120);
  const msgMediaRecRef = useRef(null);
  const msgStreamRef = useRef(null);
  const msgChunksRef = useRef([]);
  const msgTimerRef = useRef(null);
  const msgPreviewRef = useRef(null);
  const msgPhotoRef = useRef();
  const msgVideoRef = useRef();

  useEffect(() => {
    if (!loading && !user) { setAuthOpen(true); return; }
    if (user) {
      setAuthOpen(false);
      api.get("/living-candle")
        .then((r) => {
          const d = r.data; setLc(d);
          setDisplayName(d.display_name || user.name || "");
          setCitation(d.citation || "");
          setPhotos(d.photos || []);
          setMainPhoto(d.main_photo || user.avatar_base64 || null);
          setBirthDate(d.birth_date || "");
          setDeathDate(d.death_date || "");
          setNationality(d.nationality || "");
          setGarantes(padGarantes(d.garantes));
          setValidatedSteps(d.validated_steps || []);
          setMusicOn(d.music_on !== false);
          setMusicChoice(d.music_choice || "default");
          setMusicReady(!!d.music_ready);
          setMusicName(d.music_name || null);
          if (d.music_ready) loadMusicPreview("bougie");
          setCerMusicOn(d.cer_music_on !== false);
          setCerMusicChoice(d.cer_music_choice || "default");
          setCerMusicReady(!!d.cer_music_ready);
          setCerMusicName(d.cer_music_name || null);
          if (d.cer_music_ready) loadMusicPreview("ceremony");
          if (d.ceremony) setCeremony({ enabled: !!d.ceremony.enabled, payer: d.ceremony.payer || "owner", owner_paid: !!d.ceremony.owner_paid, text: d.ceremony.text || "", songs: d.ceremony.songs || [], photos: d.ceremony.photos || [], video_ready: !!d.ceremony.video_ready, files: d.ceremony.files || [], want_text: d.ceremony.want_text !== false, want_photos: d.ceremony.want_photos !== false, want_video: d.ceremony.want_video !== false, want_file: d.ceremony.want_file !== false, access_code: d.ceremony.access_code });
          if (d.pricing) setPricing(d.pricing);
          if (d.video_ready) loadVideoPreview();
          loadMessages();
        })
        .catch((e) => {
          if (e.response?.status === 403) { toast.error(t("po.tt_unavailable")); navigate("/profil"); }
        });
    }
  }, [loading, user, navigate]);

  useEffect(() => () => {
    clearInterval(recTimerRef.current);
    (recStreamRef.current?.getTracks() || []).forEach((t) => t.stop());
    clearInterval(msgTimerRef.current);
    (msgStreamRef.current?.getTracks() || []).forEach((t) => t.stop());
  }, []);

  useEffect(() => {
    if (!showSlideshow || photos.length < 2) return;
    const id = setInterval(() => setSlide((s) => (s + 1) % photos.length), 3000);
    return () => clearInterval(id);
  }, [showSlideshow, photos.length]);

  useEffect(() => {
    const cp = ceremony.photos || [];
    if (!showCerSlideshow || cp.length < 2) return;
    const id = setInterval(() => setCerSlide((s) => (s + 1) % cp.length), 3000);
    return () => clearInterval(id);
  }, [showCerSlideshow, ceremony.photos]);

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    if (photos.length + files.length > MAX_PHOTOS) { toast.error(t("po.tt_max_photos", { n: MAX_PHOTOS })); return; }
    setBusy(true);
    try {
      const compressed = [];
      for (const f of files) compressed.push(await fileToCompressedDataUrl(f));
      const next = [...photos, ...compressed];
      setPhotos(next);
      await save({ photos: next });
    } catch { toast.error(t("po.tt_import_photo_fail")); }
    finally { setBusy(false); }
  };

  const removePhoto = async (i) => {
    if (!askDelete("del_photo")) return;
    const next = photos.filter((_, idx) => idx !== i);
    setPhotos(next); setSlide(0);
    await save({ photos: next });
  };

  const movePhoto = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= photos.length) return;
    const next = [...photos];
    [next[i], next[j]] = [next[j], next[i]];
    setPhotos(next);
    await save({ photos: next });
  };

  const loadVideoPreview = async () => {
    try {
      const r = await api.get("/living-candle/video", { responseType: "blob" });
      setVideoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(r.data); });
    } catch { /* pas de vidéo */ }
  };

  const loadMusicPreview = async (target = "bougie") => {
    try {
      const r = await api.get(`/living-candle/music?target=${target}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (target === "ceremony") setCerMusicUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      else setMusicUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch { /* pas de musique */ }
  };

  const STEP_ORDER = ["ceremonie", "message", "bougie", "publication"];
  const messageSkipped = validatedSteps.includes("message") && messages.length === 0;
  const toggleNoMessage = async () => {
    const willSkip = !messageSkipped;
    const next = willSkip
      ? (validatedSteps.includes("message") ? validatedSteps : [...validatedSteps, "message"])
      : validatedSteps.filter((k) => k !== "message");
    setValidatedSteps(next);
    try { await api.put("/living-candle", { validated_steps: next }); } catch { toast.error(t("po.tt_save_fail")); }
    if (willSkip) {
      setActiveTab("bougie");
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success(t("po.vs_toast"));
    }
  };
  const validateAndNext = async (key) => {
    const next = validatedSteps.includes(key) ? validatedSteps : [...validatedSteps, key];
    setValidatedSteps(next);
    const body = key === "bougie"
      ? { display_name: displayName, citation, photos, main_photo: mainPhoto, birth_date: birthDate || null, death_date: deathDate || null, nationality: nationality || null, validated_steps: next }
      : { validated_steps: next };
    try { const r = await api.put("/living-candle", body); setLc(r.data); }
    catch { toast.error(t("po.tt_save_fail")); }
    if (key === "publication") {
      const list = garantes.filter((g) => (g.email || "").trim());
      if (list.length) {
        try {
          const rg = await api.post("/living-candle/garantes", { garantes: list.map((g) => ({ name: g.name, email: g.email })) });
          setLc(rg.data); setGarantes(padGarantes(rg.data.garantes));
        } catch { /* garants facultatifs */ }
      }
    }
    const idx = STEP_ORDER.indexOf(key);
    if (idx < STEP_ORDER.length - 1) {
      setActiveTab(STEP_ORDER[idx + 1]);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success(t("po.tt_step_validated"));
    } else {
      toast.success(t("po.t_all_validated"));
    }
  };

  const toggleMusicOn = async (target = "bougie") => {
    const cur = target === "ceremony" ? cerMusicOn : musicOn;
    const next = !cur;
    if (target === "ceremony") setCerMusicOn(next); else setMusicOn(next);
    try { await api.put("/living-candle", target === "ceremony" ? { cer_music_on: next } : { music_on: next }); }
    catch { toast.error(t("po.tt_save_fail")); if (target === "ceremony") setCerMusicOn(cur); else setMusicOn(cur); }
  };

  const chooseMusic = async (target, id) => {
    if (target === "ceremony") setCerMusicChoice(id); else setMusicChoice(id);
    try { await api.put("/living-candle", target === "ceremony" ? { cer_music_choice: id } : { music_choice: id }); }
    catch { toast.error(t("po.tt_save_fail")); }
  };

  const uploadMusic = async (e, target = "bougie") => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const okType = file.type.startsWith("audio/") || file.type === "video/mp4" || /\.(mp3|mp4|m4a)$/i.test(file.name || "");
    if (!okType) { toast.error(t("po.tt_mp3_mp4")); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error(t("po.tt_file_too_big20")); return; }
    const setBusyF = target === "ceremony" ? setCerMusicBusy : setMusicBusy;
    const setProgF = target === "ceremony" ? setCerMusicProgress : setMusicProgress;
    setBusyF(true); setProgF(0);
    try {
      const { data: init } = await api.post("/living-candle/music/init");
      const CHUNK = 1024 * 1024;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("upload_id", init.upload_id);
        fd.append("index", String(i));
        fd.append("chunk", blob, "chunk");
        await api.post("/living-candle/music/chunk", fd);
        setProgF(Math.round(((i + 1) / total) * 100));
      }
      const fd2 = new FormData();
      fd2.append("upload_id", init.upload_id);
      fd2.append("filename", file.name || "musique.mp3");
      fd2.append("content_type", file.type || "audio/mpeg");
      fd2.append("target", target);
      const { data } = await api.post("/living-candle/music/complete", fd2);
      setLc(data);
      if (target === "ceremony") { setCerMusicReady(true); setCerMusicName(data.cer_music_name || file.name); setCerMusicOn(true); setCerMusicChoice("own"); }
      else { setMusicReady(true); setMusicName(data.music_name || file.name); setMusicOn(true); setMusicChoice("own"); }
      await loadMusicPreview(target);
      toast.success(t("po.tt_music_added"));
    } catch (err) { toast.error(err.response?.data?.detail || t("po.tt_send_audio_fail")); }
    finally { setBusyF(false); setProgF(0); }
  };

  const removeMusic = async (target = "bougie") => {
    if (!window.confirm(t("po.c_del_music"))) return;
    try {
      const r = await api.delete(`/living-candle/music?target=${target}`);
      setLc(r.data);
      if (target === "ceremony") { setCerMusicReady(false); setCerMusicName(null); setCerMusicChoice("default"); setCerMusicUrl((p) => { if (p) URL.revokeObjectURL(p); return null; }); }
      else { setMusicReady(false); setMusicName(null); setMusicChoice("default"); setMusicUrl((p) => { if (p) URL.revokeObjectURL(p); return null; }); }
      toast.success(t("po.tt_music_deleted"));
    } catch { toast.error(t("po.tt_delete_fail")); }
  };

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error(t("po.tt_video_required")); return; }
    if (file.size > 200 * 1024 * 1024) { toast.error(t("po.tt_video_too_big")); return; }
    setVideoBusy(true); setVideoProgress(0);
    try {
      const { data: init } = await api.post("/living-candle/video/init");
      const CHUNK = 1024 * 1024; // 1 Mo
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("upload_id", init.upload_id);
        fd.append("index", String(i));
        fd.append("chunk", blob, "chunk");
        await api.post("/living-candle/video/chunk", fd);
        setVideoProgress(Math.round(((i + 1) / total) * 100));
      }
      const fd2 = new FormData();
      fd2.append("upload_id", init.upload_id);
      fd2.append("filename", file.name || "video.mp4");
      fd2.append("content_type", file.type || "video/mp4");
      const { data } = await api.post("/living-candle/video/complete", fd2);
      setLc(data);
      await loadVideoPreview();
      toast.success(t("po.tt_video_saved_e"));
    } catch (err) { toast.error(err.response?.data?.detail || t("po.tt_send_video_fail")); }
    finally { setVideoBusy(false); setVideoProgress(0); }
  };

  const removeVideo = async () => {
    if (!window.confirm(t("po.c_del_video"))) return;
    try {
      const r = await api.delete("/living-candle/video");
      setLc(r.data);
      setVideoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      toast.success(t("po.tt_video_deleted"));
    } catch { toast.error(t("po.tt_delete_fail")); }
  };

  const save = async (patch) => {
    setSaving(true);
    try {
      const body = { display_name: displayName, citation, photos, main_photo: mainPhoto, birth_date: birthDate || null, death_date: deathDate || null, nationality: nationality || null, ...patch };
      const r = await api.put("/living-candle", body);
      setLc(r.data);
      if (!patch) toast.success(t("po.tt_saved"));
    } catch { toast.error(t("po.tt_save_fail")); }
    finally { setSaving(false); }
  };

  const uploadMainPhoto = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("po.tt_image_required")); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error(t("po.tt_photo_too_big")); return; }
    setBusy(true);
    try {
      const compressed = await fileToCompressedDataUrl(file);
      setMainPhoto(compressed);
      await save({ main_photo: compressed });
      toast.success(t("po.tt_photo_saved"));
    } catch { toast.error(t("po.tt_import_fail")); }
    finally { setBusy(false); }
  };

  const saveGarantesBtn = async () => {
    const list = garantes.filter((g) => (g.email || "").trim());
    setBusy(true);
    try {
      if (list.length) {
        const r = await api.post("/living-candle/garantes", { garantes: list.map((g) => ({ name: g.name, email: g.email })) });
        setLc(r.data); setGarantes(padGarantes(r.data.garantes));
      }
      toast.success(t("po.tt_saved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("po.tt_error")); }
    finally { setBusy(false); }
  };

  const updateGarante = (i, field, val) => setGarantes((gs) => gs.map((g, idx) => idx === i ? { ...g, [field]: val } : g));
  const removeGaranteRow = (i) => setGarantes((gs) => gs.map((g, idx) => idx === i ? { name: "", email: "" } : g));

  const copyPublishLink = async (code) => {
    const link = `${window.location.origin}/hommage/${code}`;
    try { await navigator.clipboard.writeText(link); toast.success(t("po.tt_link_garant")); } catch { toast.error(t("po.tt_copy_fail")); }
  };

  // ---- Cérémonie ----
  const saveCeremony = async (patch = {}) => {
    const cur = { ...ceremonyRef.current, ...patch };
    ceremonyRef.current = cur;
    const body = { enabled: true, payer: cur.payer || "owner", text: cur.text || "", songs: cur.songs || [], photos: cur.photos || [], want_text: cur.want_text !== false, want_photos: cur.want_photos !== false, want_video: cur.want_video !== false, want_file: cur.want_file !== false };
    try {
      const r = await api.put("/living-candle/ceremony", body);
      const c = r.data.ceremony || {};
      const merged = { enabled: !!c.enabled, payer: c.payer, owner_paid: !!c.owner_paid, text: c.text || "", songs: c.songs || [], photos: c.photos || [], video_ready: !!c.video_ready, files: c.files || [], want_text: c.want_text !== false, want_photos: c.want_photos !== false, want_video: c.want_video !== false, want_file: c.want_file !== false, access_code: c.access_code };
      ceremonyRef.current = merged;
      setCeremony(merged);
      toast.success(t("po.tt_cer_saved"));
    } catch (e) { toast.error(e.response?.data?.detail || t("po.tt_save_retry")); }
  };

  // Enregistre TOUTE la configuration de la cérémonie (texte + chansons + photos + musique)
  const saveCeremonyAll = async () => {
    if (recording) stopCeremonyRecording(); else if (camOpen) cancelCeremonyCamera();
    const cur = { ...ceremonyRef.current };
    const body = { enabled: true, payer: cur.payer || "owner", text: cur.text || "", songs: cur.songs || [], photos: cur.photos || [], want_text: cur.want_text !== false, want_photos: cur.want_photos !== false, want_video: cur.want_video !== false, want_file: cur.want_file !== false };
    try {
      const r = await api.put("/living-candle/ceremony", body);
      const c = r.data.ceremony || {};
      const merged = { enabled: !!c.enabled, payer: c.payer, owner_paid: !!c.owner_paid, text: c.text || "", songs: c.songs || [], photos: c.photos || [], video_ready: !!c.video_ready, files: c.files || [], want_text: c.want_text !== false, want_photos: c.want_photos !== false, want_video: c.want_video !== false, want_file: c.want_file !== false, access_code: c.access_code };
      ceremonyRef.current = merged; setCeremony(merged);
      await api.put("/living-candle", { cer_music_choice: cerMusicChoice, cer_music_on: cerMusicOn });
      toast.success(t("po.tt_cer_saved_full"));
    } catch (e) { toast.error(e.response?.data?.detail || t("po.tt_save_retry")); }
  };

  const addSong = () => {
    const s = (songInput || "").trim();
    if (!s) return;
    const songs = [...(ceremony.songs || []), s].slice(0, 20);
    setSongInput("");
    setCeremony((p) => ({ ...p, songs }));
    saveCeremony({ songs });
  };
  const removeSong = (i) => {
    const songs = (ceremony.songs || []).filter((_, idx) => idx !== i);
    setCeremony((p) => ({ ...p, songs }));
    saveCeremony({ songs });
  };

  const addCeremonyPhotos = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    if ((ceremony.photos?.length || 0) + files.length > MAX_PHOTOS) { toast.error(t("po.tt_max_photos", { n: MAX_PHOTOS })); return; }
    const compressed = [];
    for (const f of files) compressed.push(await fileToCompressedDataUrl(f));
    const photos = [...(ceremony.photos || []), ...compressed];
    setCeremony((p) => ({ ...p, photos }));
    saveCeremony({ photos });
  };
  const removeCeremonyPhoto = (i) => {
    if (!askDelete("del_photo")) return;
    const photos = (ceremony.photos || []).filter((_, idx) => idx !== i);
    setCeremony((p) => ({ ...p, photos }));
    saveCeremony({ photos });
  };
  const moveCeremonyPhoto = (i, dir) => {
    const list = [...(ceremony.photos || [])];
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    setCeremony((p) => ({ ...p, photos: list }));
    saveCeremony({ photos: list });
  };
  const dragFrom = useRef(null);
  const reorderList = (list, from, to) => { const n = [...list]; const [m] = n.splice(from, 1); n.splice(to, 0, m); return n; };
  const dropBougie = (to) => { const from = dragFrom.current; dragFrom.current = null; if (from == null || from === to) return; const next = reorderList(photos, from, to); setPhotos(next); save({ photos: next }); };
  const dropCeremony = (to) => { const from = dragFrom.current; dragFrom.current = null; if (from == null || from === to) return; const next = reorderList(ceremony.photos || [], from, to); setCeremony((p) => ({ ...p, photos: next })); saveCeremony({ photos: next }); };

  const uploadCeremonyFile = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error(t("po.tt_video_too_big")); return; }
    setCerVideoBusy(true); setCerVideoProgress(0);
    try {
      const { data: init } = await api.post("/living-candle/ceremony/video/init");
      const CHUNK = 1024 * 1024;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", init.upload_id); fd.append("index", String(i));
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
        await api.post("/living-candle/video/chunk", fd);
        setCerVideoProgress(Math.round(((i + 1) / total) * 100));
      }
      const fd2 = new FormData();
      fd2.append("upload_id", init.upload_id); fd2.append("filename", file.name || "video.webm"); fd2.append("content_type", file.type || "video/webm");
      await api.post("/living-candle/ceremony/video/complete", fd2);
      setCeremony((p) => ({ ...p, video_ready: true }));
      toast.success(t("po.tt_cer_video_saved"));
    } catch (err) { toast.error(err.response?.data?.detail || t("po.tt_send_video_fail")); }
    finally { setCerVideoBusy(false); setCerVideoProgress(0); }
  };
  const uploadCeremonyVideo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error(t("po.tt_video_required")); return; }
    await uploadCeremonyFile(file);
  };
  const setCerWant = (field, val) => {
    setCeremony((p) => ({ ...p, [field]: val }));
    ceremonyRef.current = { ...ceremonyRef.current, [field]: val };
    saveCeremony({ [field]: val });
  };
  const uploadCeremonyDoc = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setCerDocBusy(true);
    try {
      for (const file of files) {
        if (file.size > 50 * 1024 * 1024) { toast.error(t("po.tt_file_too_big50", { name: file.name })); continue; }
        const { data: init } = await api.post("/living-candle/ceremony/file/init");
        const CHUNK = 1024 * 1024;
        const total = Math.ceil(file.size / CHUNK) || 1;
        for (let i = 0; i < total; i++) {
          const fd = new FormData();
          fd.append("upload_id", init.upload_id); fd.append("index", String(i));
          fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
          await api.post("/living-candle/video/chunk", fd);
        }
        const fd2 = new FormData();
        fd2.append("upload_id", init.upload_id); fd2.append("filename", file.name || "document"); fd2.append("content_type", file.type || "application/octet-stream");
        const { data } = await api.post("/living-candle/ceremony/file/complete", fd2);
        const c = data.ceremony || {};
        setCeremony((p) => ({ ...p, files: c.files || [] }));
      }
      toast.success(t("po.tt_files_saved"));
    } catch (err) { toast.error(err.response?.data?.detail || t("po.tt_send_file_fail")); }
    finally { setCerDocBusy(false); }
  };
  const removeCeremonyDoc = async (fileId) => {
    if (!askDelete("del_file")) return;
    try { const r = await api.delete(`/living-candle/ceremony/file/${fileId}`); setCeremony((p) => ({ ...p, files: (r.data.ceremony || {}).files || [] })); toast.success(t("po.tt_file_deleted")); } catch { toast.error(t("po.tt_delete_fail")); }
  };
  const openCeremonyDoc = async (fileId, name) => {
    try {
      const r = await api.get(`/living-candle/ceremony/file/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.rel = "noopener";
      a.download = name || "document"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error(t("po.tt_open_fail")); }
  };
  const stopCeremonyRecording = () => {
    try { if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") mediaRecRef.current.stop(); } catch { /* ignore */ }
  };
  const openCeremonyCamera = async () => {
    if (camOpen) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") { toast.error(t("po.tt_cam_unsupported")); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: HQ_VIDEO_CONSTRAINTS, audio: true });
      recStreamRef.current = stream;
      recChunksRef.current = [];
      setCamOpen(true); setRecording(false); setRecLeft(MAX_VIDEO_SEC);
      setTimeout(() => { if (recPreviewRef.current) { recPreviewRef.current.srcObject = stream; recPreviewRef.current.play?.().catch(() => {}); } }, 60);
    } catch { toast.error(t("po.tt_cam_denied")); setCamOpen(false); }
  };
  const startRecording = () => {
    const stream = recStreamRef.current;
    if (!stream || recording) return;
    recChunksRef.current = [];
    const cand = ["video/mp4;codecs=avc1.640028,mp4a.40.2", "video/mp4;codecs=h264,aac", "video/mp4;codecs=avc1", "video/mp4", "video/webm;codecs=h264,opus", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
    const mime = cand.find((t) => { try { return MediaRecorder.isTypeSupported(t); } catch { return false; } }) || "";
    const mr = new MediaRecorder(stream, recorderOptions(stream, mime));
    mediaRecRef.current = mr;
    mr.ondataavailable = (ev) => { if (ev.data && ev.data.size) recChunksRef.current.push(ev.data); };
    mr.onstop = async () => {
      clearInterval(recTimerRef.current);
      (recStreamRef.current?.getTracks() || []).forEach((t) => t.stop());
      if (recPreviewRef.current) recPreviewRef.current.srcObject = null;
      setRecording(false); setCamOpen(false);
      const actualType = mr.mimeType || mime || "video/webm";
      const ext = actualType.includes("mp4") ? "mp4" : "webm";
      const blob = new Blob(recChunksRef.current, { type: actualType });
      recChunksRef.current = [];
      if (blob.size) { const f = new File([blob], `ceremonie_${Date.now()}.${ext}`, { type: actualType }); await uploadCeremonyFile(f); }
      else { toast.error(t("po.tt_no_video")); }
    };
    mr.start(1000);
    setRecording(true); setRecLeft(MAX_VIDEO_SEC);
    recTimerRef.current = setInterval(() => {
      setRecLeft((s) => { if (s <= 1) { stopCeremonyRecording(); return 0; } return s - 1; });
    }, 1000);
  };
  const cancelCeremonyCamera = () => {
    clearInterval(recTimerRef.current);
    (recStreamRef.current?.getTracks() || []).forEach((t) => t.stop());
    if (recPreviewRef.current) recPreviewRef.current.srcObject = null;
    recChunksRef.current = [];
    setRecording(false); setCamOpen(false);
  };
  const removeCeremonyVideo = async () => {
    try { await api.delete("/living-candle/ceremony/video"); setCeremony((p) => ({ ...p, video_ready: false })); toast.success(t("po.tt_video_deleted")); } catch { toast.error(t("po.tt_delete_fail")); }
  };

  const payCeremony = async () => {
    try {
      const r = await api.post("/living-pay/checkout", { product_type: "ceremony", ref: lc.id, origin_url: window.location.origin, return_path: "/ma-bougie" });
      if (["free", "simulated", "already"].includes(r.data.mode)) { toast.success(t("po.tt_dossier_unlocked")); setCeremony((p) => ({ ...p, owner_paid: true })); }
      else submitPayment(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || t("po.tt_pay_fail")); }
  };

  const copyCeremonyLink = async () => {
    const link = `${window.location.origin}/ceremonie/${lc.id}`;
    try { await navigator.clipboard.writeText(link); toast.success(t("po.tt_link_dossier")); } catch { toast.error(t("po.tt_copy_fail")); }
  };

  // ---- Messages individuels ----
  const refreshCredits = async () => {
    try { const r = await api.get("/living-candle/prepay/status"); setCredits(r.data.message_credits || 0); setPrepaidCeremony(!!r.data.prepaid_ceremony); } catch { /* ignore */ }
  };
  const loadMessages = async () => {
    try { const r = await api.get("/living-candle/messages"); setMessages(r.data.items || []); } catch { /* ignore */ }
    refreshCredits();
  };
  const openNewMsg = () => { setEm({ id: null, recipient_name: "", recipient_email: "", title: "", text: "", photos: [], songs: [], music_choice: "default", music_on: true, songInput: "", free: false, video_ready: false, files: [], want_text: true, want_photos: true, want_video: true, want_file: true, access_code: null }); setMsgOpen(true); };
  const openEditMsg = (m) => { setEm({ ...m, photos: m.photos || [], songs: m.songs || [], music_choice: m.music_choice || "default", music_on: m.music_on !== false, files: m.files || [], want_text: m.want_text !== false, want_photos: m.want_photos !== false, want_video: m.want_video !== false, want_file: m.want_file !== false, songInput: "" }); setMsgOpen(true); };
  const addMsgSong = () => setEm((p) => { const s = (p.songInput || "").trim(); if (!s) return p; return { ...p, songs: [...(p.songs || []), s].slice(0, 20), songInput: "" }; });
  const removeMsgSong = (i) => setEm((p) => ({ ...p, songs: (p.songs || []).filter((_, idx) => idx !== i) }));

  const saveMsg = async (closeAfter = true) => {
    if (!(em.recipient_email || "").trim()) { toast.error(t("po.tt_email_indicate")); return null; }
    setMsgBusy(true);
    try {
      const body = { recipient_name: em.recipient_name, recipient_email: em.recipient_email, title: em.title, text: em.text, photos: em.photos, songs: em.songs || [], music_choice: em.music_choice || "default", music_on: em.music_on !== false, free: em.free, want_text: em.want_text !== false, want_photos: em.want_photos !== false, want_video: em.want_video !== false, want_file: em.want_file !== false };
      const r = em.id ? await api.put(`/living-candle/messages/${em.id}`, body) : await api.post("/living-candle/messages", body);
      setEm((p) => ({ ...p, ...r.data }));
      await loadMessages();
      toast.success(t("po.tt_vault"));
      if (closeAfter) setMsgOpen(false);
      return r.data;
    } catch (e) { toast.error(e.response?.data?.detail || t("po.tt_error")); return null; }
    finally { setMsgBusy(false); }
  };

  const pickMsgVideo = async () => {
    let mid = em.id;
    if (!mid) {
      if (!(em.recipient_email || "").trim()) { toast.error(t("po.tt_email_first")); return; }
      const saved = await saveMsg(false);
      if (!saved) return;
      mid = saved.id;
    }
    setTimeout(() => msgVideoRef.current?.click(), 50);
  };

  const deleteMsg = async (id) => {
    const m = messages.find((x) => x.id === id);
    const who = m?.recipient_name || m?.recipient_email || t("po.del_recipient");
    if (!window.confirm(t("po.del_msg_confirm", { who }))) return;
    if (!window.confirm(t("po.c_del_msg"))) return;
    try { await api.delete(`/living-candle/messages/${id}`); await loadMessages(); toast.success(t("po.tt_msg_deleted")); } catch { toast.error(t("po.tt_delete_fail")); }
  };

  const sendMsg = async (m) => {
    try { await api.post(`/living-candle/messages/${m.id}/send`); await loadMessages(); toast.success(t("po.tt_link_sent", { email: m.recipient_email })); } catch (e) { toast.error(e.response?.data?.detail || t("po.tt_send_fail")); }
  };

  const copyMsgLink = async (code) => {
    const link = `${window.location.origin}/message/${code}`;
    try { await navigator.clipboard.writeText(link); toast.success(t("po.tt_link_msg")); } catch { toast.error(t("po.tt_copy_fail")); }
  };

  const addMsgPhotos = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    if ((em.photos?.length || 0) + files.length > MAX_PHOTOS) { toast.error(t("po.tt_max_photos", { n: MAX_PHOTOS })); return; }
    const compressed = [];
    for (const f of files) compressed.push(await fileToCompressedDataUrl(f));
    setEm((p) => ({ ...p, photos: [...(p.photos || []), ...compressed] }));
  };
  const removeMsgPhoto = (i) => { if (!askDelete("del_photo")) return; setEm((p) => ({ ...p, photos: (p.photos || []).filter((_, idx) => idx !== i) })); };

  const uploadMsgVideoFile = async (file) => {
    if (!file || !em?.id) return;
    if (file.size > 200 * 1024 * 1024) { toast.error(t("po.tt_video_too_big")); return; }
    setMsgVideoBusy(true); setMsgVideoProgress(0);
    try {
      const { data: init } = await api.post(`/living-candle/messages/${em.id}/video/init`);
      const CHUNK = 1024 * 1024;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", init.upload_id); fd.append("index", String(i));
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
        await api.post("/living-candle/video/chunk", fd);
        setMsgVideoProgress(Math.round(((i + 1) / total) * 100));
      }
      const fd2 = new FormData();
      fd2.append("upload_id", init.upload_id); fd2.append("filename", file.name || "video.mp4"); fd2.append("content_type", file.type || "video/mp4");
      const { data } = await api.post(`/living-candle/messages/${em.id}/video/complete`, fd2);
      setEm((p) => ({ ...p, ...data })); await loadMessages();
      toast.success(t("po.tt_msg_video_saved"));
    } catch (err) { toast.error(err.response?.data?.detail || t("po.tt_send_video_fail")); }
    finally { setMsgVideoBusy(false); setMsgVideoProgress(0); }
  };
  const uploadMsgVideo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !em?.id) { if (!em?.id) toast.error(t("po.tt_save_msg_first")); return; }
    if (!file.type.startsWith("video/")) { toast.error(t("po.tt_video_required")); return; }
    await uploadMsgVideoFile(file);
  };

  const pickMsgMusic = async () => {
    let mid = em.id;
    if (!mid) {
      if (!(em.recipient_email || "").trim()) { toast.error(t("po.tt_email_first")); return; }
      const saved = await saveMsg(false);
      if (!saved) return;
      mid = saved.id;
    }
    setTimeout(() => msgMusicRef.current?.click(), 50);
  };
  const uploadMsgMusic = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !em?.id) { if (!em?.id) toast.error(t("po.tt_save_msg_first")); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error(t("po.tt_music_too_big")); return; }
    setMsgMusicBusy(true); setMsgMusicProgress(0);
    try {
      const { data: init } = await api.post(`/living-candle/messages/${em.id}/music/init`);
      const CHUNK = 1024 * 1024;
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", init.upload_id); fd.append("index", String(i));
        fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
        await api.post("/living-candle/music/chunk", fd);
        setMsgMusicProgress(Math.round(((i + 1) / total) * 100));
      }
      const fd2 = new FormData();
      fd2.append("upload_id", init.upload_id); fd2.append("filename", file.name || "musique.mp3"); fd2.append("content_type", file.type || "audio/mpeg");
      const { data } = await api.post(`/living-candle/messages/${em.id}/music/complete`, fd2);
      setEm((p) => ({ ...p, ...data })); await loadMessages();
      toast.success(t("po.tt_msg_music_saved"));
    } catch (err) { toast.error(err.response?.data?.detail || t("po.tt_send_music_fail")); }
    finally { setMsgMusicBusy(false); setMsgMusicProgress(0); }
  };
  const removeMsgMusic = async () => {
    if (!em?.id) { setEm((p) => ({ ...p, music_ready: false, music_choice: "default" })); return; }
    if (!askDelete("del_music")) return;
    try {
      const { data } = await api.delete(`/living-candle/messages/${em.id}/music`);
      setEm((p) => ({ ...p, ...data })); await loadMessages();
      toast.success(t("po.tt_music_deleted"));
    } catch { toast.error(t("po.tt_delete_fail")); }
  };
  const stopMsgRecording = () => {
    try { if (msgMediaRecRef.current && msgMediaRecRef.current.state !== "inactive") msgMediaRecRef.current.stop(); } catch { /* ignore */ }
  };
  const cancelMsgCamera = () => {
    clearInterval(msgTimerRef.current);
    (msgStreamRef.current?.getTracks() || []).forEach((t) => t.stop());
    if (msgPreviewRef.current) msgPreviewRef.current.srcObject = null;
    msgChunksRef.current = [];
    setMsgRecording(false); setMsgCamOpen(false);
  };
  const openMsgCamera = async () => {
    if (msgCamOpen) return;
    if (!em?.id) {
      if (!(em?.recipient_email || "").trim()) { toast.error(t("po.tt_email_first")); return; }
      const saved = await saveMsg(false);
      if (!saved) return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") { toast.error(t("po.tt_cam_unsupported")); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: HQ_VIDEO_CONSTRAINTS, audio: true });
      msgStreamRef.current = stream; msgChunksRef.current = [];
      setMsgCamOpen(true); setMsgRecording(false); setMsgRecLeft(MAX_VIDEO_SEC);
      setTimeout(() => { if (msgPreviewRef.current) { msgPreviewRef.current.srcObject = stream; msgPreviewRef.current.play?.().catch(() => {}); } }, 60);
    } catch { toast.error(t("po.tt_cam_denied")); setMsgCamOpen(false); }
  };
  const startMsgRecording = () => {
    const stream = msgStreamRef.current;
    if (!stream || msgRecording) return;
    msgChunksRef.current = [];
    const cand = ["video/mp4;codecs=avc1.640028,mp4a.40.2", "video/mp4;codecs=h264,aac", "video/mp4;codecs=avc1", "video/mp4", "video/webm;codecs=h264,opus", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
    const mime = cand.find((t) => { try { return MediaRecorder.isTypeSupported(t); } catch { return false; } }) || "";
    const mr = new MediaRecorder(stream, recorderOptions(stream, mime));
    msgMediaRecRef.current = mr;
    mr.ondataavailable = (ev) => { if (ev.data && ev.data.size) msgChunksRef.current.push(ev.data); };
    mr.onstop = async () => {
      clearInterval(msgTimerRef.current);
      (msgStreamRef.current?.getTracks() || []).forEach((t) => t.stop());
      if (msgPreviewRef.current) msgPreviewRef.current.srcObject = null;
      setMsgRecording(false); setMsgCamOpen(false);
      const actualType = mr.mimeType || mime || "video/webm";
      const ext = actualType.includes("mp4") ? "mp4" : "webm";
      const blob = new Blob(msgChunksRef.current, { type: actualType });
      msgChunksRef.current = [];
      if (blob.size) { const f = new File([blob], `message_${Date.now()}.${ext}`, { type: actualType }); await uploadMsgVideoFile(f); }
      else { toast.error(t("po.tt_no_video")); }
    };
    mr.start(1000);
    setMsgRecording(true); setMsgRecLeft(MAX_VIDEO_SEC);
    msgTimerRef.current = setInterval(() => {
      setMsgRecLeft((s) => { if (s <= 1) { stopMsgRecording(); return 0; } return s - 1; });
    }, 1000);
  };
  const removeMsgVideo = async () => {
    if (!askDelete("del_video")) return;
    try { const r = await api.delete(`/living-candle/messages/${em.id}/video`); setEm((p) => ({ ...p, ...r.data })); await loadMessages(); toast.success(t("po.tt_video_deleted")); } catch { toast.error(t("po.tt_delete_fail")); }
  };

  const pickMsgDoc = async () => {
    let mid = em.id;
    if (!mid) {
      if (!(em.recipient_email || "").trim()) { toast.error(t("po.tt_email_first")); return; }
      const saved = await saveMsg(false);
      if (!saved) return;
    }
    setTimeout(() => msgDocRef.current?.click(), 50);
  };
  const uploadMsgDoc = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length || !em?.id) { if (!em?.id) toast.error(t("po.tt_save_msg_first")); return; }
    setMsgDocBusy(true);
    try {
      for (const file of files) {
        if (file.size > 50 * 1024 * 1024) { toast.error(t("po.tt_file_too_big50", { name: file.name })); continue; }
        const { data: init } = await api.post(`/living-candle/messages/${em.id}/file/init`);
        const CHUNK = 1024 * 1024;
        const total = Math.ceil(file.size / CHUNK) || 1;
        for (let i = 0; i < total; i++) {
          const fd = new FormData();
          fd.append("upload_id", init.upload_id); fd.append("index", String(i));
          fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), "chunk");
          await api.post("/living-candle/video/chunk", fd);
        }
        const fd2 = new FormData();
        fd2.append("upload_id", init.upload_id); fd2.append("filename", file.name || "document"); fd2.append("content_type", file.type || "application/octet-stream");
        const { data } = await api.post(`/living-candle/messages/${em.id}/file/complete`, fd2);
        setEm((p) => ({ ...p, files: data.files || [] }));
      }
      await loadMessages();
      toast.success(t("po.tt_files_saved"));
    } catch (err) { toast.error(err.response?.data?.detail || t("po.tt_send_file_fail")); }
    finally { setMsgDocBusy(false); }
  };
  const removeMsgDoc = async (fileId) => {
    if (!askDelete("del_file")) return;
    try { const r = await api.delete(`/living-candle/messages/${em.id}/file/${fileId}`); setEm((p) => ({ ...p, files: r.data.files || [] })); await loadMessages(); toast.success(t("po.tt_file_deleted")); } catch { toast.error(t("po.tt_delete_fail")); }
  };
  const openMsgDoc = async (fileId, name) => {
    try {
      const r = await api.get(`/living-candle/messages/${em.id}/file/${fileId}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.rel = "noopener";
      a.download = name || "document"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error(t("po.tt_open_fail")); }
  };

  if (!loading && !user) {
    return (
      <div className="min-h-screen living-sky">
        <div className="living-sky-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/living-sky.jpg)` }} aria-hidden="true" />
        <Header />
        <div className="relative z-10 mx-auto flex max-w-lg flex-col items-center px-6 py-20 text-center">
          <img src={`${process.env.PUBLIC_URL}/logo-postinity.png`} alt="Postinity" className="mb-6 h-24 w-auto" style={{ filter: "drop-shadow(0 5px 8px rgba(0,0,0,0.7))" }} />
          <h1 className="text-2xl font-semibold text-white sm:text-3xl">{t("po.auth_title")}</h1>
          <p className="mt-3 text-sm text-white/80">{t("po.auth_sub")}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button className="rounded-full bg-sky-500 px-6 text-white hover:bg-sky-400" onClick={() => { setAuthMode("register"); setAuthOpen(true); }} data-testid="postinity-register-btn">{t("po.auth_register")}</Button>
            <Button variant="outline" className="rounded-full border-white/30 bg-slate-900/40 px-6 text-white hover:bg-slate-900/60" onClick={() => { setAuthMode("login"); setAuthOpen(true); }} data-testid="postinity-login-btn">{t("po.auth_login")}</Button>
          </div>
        </div>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} onSuccess={() => setAuthOpen(false)} />
      </div>
    );
  }

  if (loading || !lc) {
    return <div className="min-h-screen living-sky"><div className="living-sky-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/living-sky.jpg)` }} aria-hidden="true" /><Header /><div className="relative z-10 flex justify-center py-20"><Loader2 className="animate-spin text-white" /></div></div>;
  }

  const done = {
    ceremonie: validatedSteps.includes("ceremonie"),
    message: validatedSteps.includes("message"),
    bougie: validatedSteps.includes("bougie"),
    publication: validatedSteps.includes("publication"),
  };

  const allGreen = STEP_ORDER.every((k) => validatedSteps.includes(k));
  const activateGarantes = async () => {
    const turningOn = !lc.garantes_active;
    if (turningOn) {
      if (!allGreen) { toast.info(t("po.ga_guard")); return; }
      if (!garantes.some((g) => (g.email || "").trim())) { toast.info(t("po.tt_add_guarantor")); return; }
      setShowGarantPreview(true);
      return;
    }
    if (!window.confirm(t("po.c_deact_garants"))) return;
    if (!window.confirm(t("po.deact_confirm2"))) return;
    setBusy(true);
    try {
      const r = await api.post("/living-candle/garantes/activate", { active: false });
      setLc(r.data); toast.success(t("po.tt_garants_off"));
    } catch (e) { toast.error(e.response?.data?.detail || t("po.tt_action_fail")); }
    finally { setBusy(false); }
  };
  const confirmActivateGarantes = async () => {
    setShowGarantPreview(false);
    if (!window.confirm(t("po.ga_confirm_send"))) return;
    setBusy(true);
    try {
      const r = await api.post("/living-candle/garantes/activate", { active: true });
      setLc(r.data);
      toast.success(t("po.tt_garants_activated", { n: r.data._sent || 0 }));
    } catch (e) { toast.error(e.response?.data?.detail || t("po.tt_action_fail")); }
    finally { setBusy(false); }
  };
  const activateCeremony = async () => {
    const turningOn = !lc.ceremony_active;
    if (turningOn && !allGreen) { toast.info(t("po.t_cer_guard")); return; }
    if (turningOn) {
      if (!window.confirm(t("po.c_act_cer1"))) return;
      if (!window.confirm(t("po.c_confirm2"))) return;
    } else {
      if (!window.confirm(t("po.c_deact_cer"))) return;
      if (!window.confirm(t("po.c_deact2"))) return;
    }
    setBusy(true);
    try {
      const r = await api.post("/living-candle/ceremony/activate", { active: turningOn });
      setLc(r.data);
      toast.success(turningOn ? t("po.tt_cer_activated") : t("po.tt_cer_deactivated"));
    } catch (e) { toast.error(e.response?.data?.detail || t("po.tt_action_fail")); }
    finally { setBusy(false); }
  };


  return (
    <div className="living-sky relative min-h-screen pb-36">
      <div className="living-sky-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/living-sky.jpg)` }} aria-hidden="true" />
      <div className="sky-clouds" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/sky-clouds.png)` }} aria-hidden="true" />
      <div className="sky-clouds layer2" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/sky-clouds.png)` }} aria-hidden="true" />
      <Header />
      <div className="relative z-10 mx-auto max-w-2xl px-5 py-6" data-testid="living-candle-page">
        <button onClick={() => navigate("/profil")} className="mb-4 inline-flex items-center gap-1 rounded-full border border-white/30 bg-slate-900/50 px-3 py-1.5 text-sm text-white/90 backdrop-blur hover:bg-slate-900/70" data-testid="living-back-btn"><ChevronLeft size={15} /> {t("po.back_profile")}</button>

        <div className="-mt-16 mb-0 flex justify-center" data-testid="postinity-logo">
          <img src={`${process.env.PUBLIC_URL}/logo-postinity.png`} alt="Postinity" className="h-20 w-auto opacity-95 sm:h-28" style={{ filter: "drop-shadow(0 5px 8px rgba(0,0,0,0.7)) drop-shadow(0 2px 4px rgba(0,0,0,0.75))" }} />
        </div>

        <div className="relative mb-5 rounded-3xl border border-white/25 bg-slate-900/55 p-5 shadow-xl backdrop-blur-xl">
          <img src={`${process.env.PUBLIC_URL}/cloud-lock.png`} alt="" aria-hidden="true" className="pointer-events-none absolute bottom-2 right-1 w-24 opacity-100 sm:w-28" data-testid="living-cloudlock-icon" />
          <div className="relative mb-3 flex flex-wrap justify-end gap-2" data-testid="living-activate-buttons">
            <button type="button" onClick={activateGarantes} disabled={busy}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${lc.garantes_active ? "border-emerald-400 bg-emerald-500/20 text-emerald-200" : "border-white/25 bg-white/5 text-white/80 hover:bg-white/10"}`}
              data-testid="living-activate-garantes-btn">
              <ShieldCheck size={13} /> {lc.garantes_active ? t("po.lc_act_garantes_on") : t("po.lc_act_garantes_off")}
            </button>
            <button type="button" onClick={activateCeremony} disabled={busy}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${lc.ceremony_active ? "border-emerald-400 bg-emerald-500/20 text-emerald-200" : "border-white/25 bg-white/5 text-white/80 hover:bg-white/10"}`}
              data-testid="living-activate-ceremony-btn">
              <Lock size={13} /> {lc.ceremony_active ? t("po.lc_act_ceremony_on") : t("po.lc_act_ceremony_off")}
            </button>
          </div>
          <div className="relative flex items-center gap-2 text-amber-300"><Flame size={20} /><h1 className="text-lg font-semibold">{t("po.lc_title")} <span className="text-sm font-normal text-sky-300">{t("po.lc_by")}</span></h1></div>
          <p className="relative mt-1 text-sm leading-relaxed text-white/90 pr-24 sm:pr-28">
            {t("po.lc_intro")}
            {" "}<span className="inline-flex items-center gap-1 text-white/85"><Lock size={12} /> {t("po.lc_private")}</span>
          </p>
          <p className="relative mt-2 inline-flex max-w-[72%] items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-200" data-testid="living-save-anytime">
            <Check size={13} /> {t("po.lc_save_anytime")}
          </p>
        </div>

        {lc.published && (
          <div className="mb-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200" data-testid="living-published-banner">
            {t("po.lc_published")}{" "}
            <button className="underline" onClick={() => navigate(`/candle/${lc.published_candle_id}`)}>{t("po.lc_see_candle")}</button>
          </div>
        )}

        <Dialog open={showGarantPreview} onOpenChange={setShowGarantPreview}>
          <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="garant-email-preview-dialog">
            <DialogHeader>
              <DialogTitle>{t("po.gp_title")}</DialogTitle>
            </DialogHeader>
            <div className="rounded-xl border bg-white p-5 text-sm text-slate-800">
              <div className="mb-4 text-center">
                <img src={`${process.env.PUBLIC_URL}/logo-postinity.png`} alt="Postinity" className="mx-auto h-12 w-auto" />
              </div>
              <p className="mb-2">{t("po.gp_hello")} <span className="font-medium">{t("po.gp_name_ph")}</span>,</p>
              <p className="mb-2"><span className="font-medium">{lc.display_name || t("po.gp_you")}</span> {t("po.gp_designated")}</p>
              <p className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">{t("po.gp_unique")}</p>
              <p className="mb-2">{t("po.gp_three")}</p>
              <p className="mb-3">{t("po.gp_thanks")}</p>
              <p className="mb-3 text-center"><span className="inline-block rounded-full bg-[#f5b301] px-5 py-2 font-bold text-black">{t("po.gp_access")}</span></p>
              <p className="mb-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2">{t("po.gp_free")}</p>
              <p className="mb-3">{t("po.gp_keep")}</p>
              <p className="text-xs text-slate-500">{t("po.gp_team")}<br />{t("po.lc_by")}</p>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setShowGarantPreview(false)} data-testid="garant-preview-cancel">{t("po.cancel")}</Button>
              <Button className="rounded-full" onClick={confirmActivateGarantes} disabled={busy} data-testid="garant-preview-send"><Send size={14} className="mr-1.5" /> {t("po.gp_send")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="living-tabs">
          <TabsList className="grid h-auto w-full grid-cols-4 gap-2 bg-transparent p-0" data-testid="living-tabslist">
            <LcTab value="ceremonie" n={1} label={t("po.tab_ceremonie")} Icon={Flame} done={done.ceremonie} />
            <LcTab value="message" n={2} label={t("po.tab_message")} Icon={Mail} done={done.message} />
            <LcTab value="bougie" n={3} label={t("po.tab_bougie")} Icon={Heart} done={done.bougie} />
            <LcTab value="publication" n={4} label={t("po.tab_garants")} Icon={ShieldCheck} done={done.publication} />
          </TabsList>

          {done.ceremonie && done.message && done.bougie && done.publication && (
            <div className="mt-4 flex items-center gap-3 rounded-2xl border border-emerald-400/50 bg-slate-900/55 px-4 py-3 text-sm text-emerald-100 shadow-xl backdrop-blur-xl" data-testid="living-all-done-banner">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-black"><Heart size={18} /></span>
              <div>
                <div className="font-semibold text-emerald-200">{t("po.all_done_title")}</div>
                <div className="text-xs text-emerald-100/80">{t("po.all_done_desc")}</div>
              </div>
            </div>
          )}

        {/* 3. Ma bougie */}
        <TabsContent value="bougie" className="mt-4 rounded-2xl border border-white/20 bg-slate-900/55 px-4 pb-4 pt-4 backdrop-blur-xl shadow-xl" data-testid="acc-bougie">
        <StepRail steps={[
          { n: 1, label: t("po.b_step_photo"), done: !!mainPhoto },
          { n: 2, label: t("po.b_step_identity"), done: !!(displayName || "").trim() },
          { n: 3, label: t("po.b_step_message"), done: !!(citation || "").trim() },
        ]} />

        <p className="mb-4 text-xs text-white/70">{t("po.b_intro")}</p>

        <section className="mb-5 space-y-4">
          {/* Photo unique (par défaut : photo de profil) */}
          <div className="flex flex-col items-center gap-3">
            <button type="button" onClick={() => mainPhotoRef.current?.click()} disabled={busy} className="relative h-32 w-32 overflow-hidden rounded-full border-2 border-white/25 bg-black/40 shadow-lg transition hover:border-sky-300" data-testid="living-main-photo-btn">
              {mainPhoto ? (
                <img src={mainPhoto} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full flex-col items-center justify-center text-white/50"><ImagePlus size={26} /><span className="mt-1 text-[10px]">{t("po.add")}</span></span>
              )}
              {busy && <span className="absolute inset-0 flex items-center justify-center bg-black/50"><Loader2 size={20} className="animate-spin text-white" /></span>}
            </button>
            <input ref={mainPhotoRef} type="file" accept="image/*" className="hidden" onChange={uploadMainPhoto} data-testid="living-main-photo-input" />
            <span className="text-[11px] text-white/60">{t("po.b_photo_hint")}</span>
          </div>

          <div>
            <Label className={`flex items-center gap-2 ${(displayName || "").trim() ? "text-emerald-300" : "text-amber-300"}`}><StepNum n={2} done={!!(displayName || "").trim()} /> {t("po.b_name")}</Label>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={t("po.b_name_ph")} data-testid="living-name-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("po.b_birth")}</Label>
              <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} data-testid="living-birth-input" />
            </div>
            <div>
              <Label>{t("po.b_death")}</Label>
              <Input type="date" value={deathDate} onChange={(e) => setDeathDate(e.target.value)} data-testid="living-death-input" />
            </div>
          </div>

          <div>
            <Label>{t("po.b_nationality")}</Label>
            <select value={nationality} onChange={(e) => setNationality(e.target.value)} className="mt-1 w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white" data-testid="living-nationality-select">
              <option value="" className="bg-neutral-900">{t("po.b_choose")}</option>
              {NATIONALITIES.map((n) => (
                <option key={n.value} value={n.value} className="bg-neutral-900">{n.flag} {n.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className={`flex items-center gap-2 ${(citation || "").trim() ? "text-emerald-300" : "text-amber-300"}`}><StepNum n={3} done={!!(citation || "").trim()} /> {t("po.b_message")}</Label>
            <Textarea rows={4} value={citation} onChange={(e) => setCitation(e.target.value)} placeholder={t("po.b_message_ph")} data-testid="living-citation-input" />
          </div>

          <div className="flex justify-center pt-1">
            <Button className="rounded-full" onClick={() => save()} disabled={saving} data-testid="living-save-btn">
              {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Check size={15} className="mr-1.5" />} {t("po.save")}
            </Button>
          </div>
        </section>

        <ValidateStepButton validated={done.bougie} onClick={() => validateAndNext("bougie")} testid="validate-step-bougie" />
        </TabsContent>

        {/* 4. Mes garants */}
        <TabsContent value="publication" className="mt-4 rounded-2xl border border-white/20 bg-slate-900/55 px-4 pb-4 pt-4 backdrop-blur-xl shadow-xl" data-testid="acc-publication">
        <StepRail steps={[
          { n: 1, label: t("po.g_step_designate"), done: garantes.some((g) => (g.email || "").trim()) },
          { n: 2, label: t("po.g_step_send"), done: garantes.some((g) => g.code) },
          { n: 3, label: t("po.g_step_publish"), done: !!lc.published },
        ]} />
        {/* Pourquoi des garants */}
        <div className="mb-4 rounded-2xl border border-amber-400/20 bg-amber-500/[0.06] p-4 text-base sm:text-lg font-medium leading-relaxed text-amber-100/90" data-testid="garantes-why">
          <div className="mb-1 flex items-center gap-2 text-sm font-medium text-amber-200"><Users size={16} /> {t("po.g_why_title")}</div>
          {t("po.g_why_1")}<br />
          {t("po.g_why_2")}<br />
          {t("po.g_why_3")}
        </div>
        {/* Les 3 garants */}
        <section className="mb-4">
          <div className={`mb-2 flex items-center gap-2 ${garantes.some((g) => (g.email || "").trim()) ? "text-emerald-300" : "text-amber-200"}`}><StepNum n={1} done={garantes.some((g) => (g.email || "").trim())} /><span className="text-sm font-medium">{t("po.g_designate_3")}</span></div>
          <div className="space-y-3" data-testid="living-garantes-list">
            {garantes.map((g, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid="living-garante-row">
                <div className="mb-1 flex items-center gap-2 text-xs font-medium text-amber-200/80"><ShieldCheck size={13} /> {t("po.g_guarantor")} {i + 1}</div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div><Label className="text-xs">{t("po.g_name")}</Label><Input value={g.name} onChange={(e) => updateGarante(i, "name", e.target.value)} placeholder={t("po.g_name_ph")} data-testid={`living-garante-name-${i}`} /></div>
                  <div><Label className="text-xs">{t("po.g_email")}</Label><Input type="email" value={g.email} onChange={(e) => updateGarante(i, "email", e.target.value)} placeholder={t("po.g_email_ph")} data-testid={`living-garante-email-${i}`} /></div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {g.code && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-300"><Check size={11} /> {t("po.g_link_sent")}</span>}
                  {g.code && <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => copyPublishLink(g.code)} data-testid={`living-garante-copy-${i}`}><Copy size={13} className="mr-1.5" /> {t("po.g_copy_link")}</Button>}
                  {(g.name || g.email) && <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={() => removeGaranteRow(i)} data-testid={`living-garante-remove-${i}`}><X size={13} className="mr-1.5" /> {t("po.g_clear")}</Button>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-center">
            <Button className="rounded-full" onClick={saveGarantesBtn} disabled={busy} data-testid="living-garante-save-btn">
              {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Check size={15} className="mr-1.5" />} {t("po.save")}
            </Button>
          </div>
        </section>
        <ValidateStepButton validated={done.publication} onClick={() => validateAndNext("publication")} isLast testid="validate-step-publication" />
        </TabsContent>

        {/* 1. Ma cérémonie */}
        <TabsContent value="ceremonie" className="mt-4 rounded-2xl border border-white/20 bg-slate-900/55 px-4 pb-4 pt-4 backdrop-blur-xl shadow-xl" data-testid="acc-ceremonie">
        {/* Dossier de cérémonie */}
        <section className="mb-2" data-testid="living-ceremony-section">
          <StepRail steps={[
            { n: 1, label: t("po.cer_step_word"), done: !!(ceremony.text || "").trim() },
            { n: 2, label: t("po.cer_step_songs"), done: (ceremony.songs || []).length > 0 },
            { n: 3, label: t("po.cer_step_photos"), done: (ceremony.photos || []).length > 0 },
            { n: 4, label: t("po.cer_step_video"), done: !!ceremony.video_ready },
            { n: 5, label: t("po.cer_step_file"), done: (ceremony.files || []).length > 0 },
          ]} />
          <p className="mb-3 mt-1 rounded-xl bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100/90">
            {t("po.cer_intro")}
          </p>
          <div className="space-y-3">
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <Label className={`flex items-center gap-2 text-xs ${(ceremony.text || "").trim() ? "text-emerald-300" : "text-amber-300"}`}><StepNum n={1} done={!!(ceremony.text || "").trim()} /> {t("po.cer_msg_label")}</Label>
                  <WantToggle value={ceremony.want_text !== false} onChange={(v) => setCerWant("want_text", v)} testid="ceremony-want-text" />
                </div>
                {ceremony.want_text !== false
                  ? <Textarea rows={6} value={ceremony.text} onChange={(e) => setCeremony((p) => ({ ...p, text: e.target.value }))} onBlur={() => saveCeremony()} placeholder={t("po.cer_msg_placeholder")} data-testid="ceremony-text-input" />
                  : <p className="rounded-lg border border-dashed border-white/15 px-3 py-2 text-[11px] text-muted-foreground" data-testid="ceremony-text-off">{t("po.cer_msg_off")}</p>}
              </div>

              {/* Titres de chanson */}
              <div>
                <Label className={`text-xs flex items-center gap-1.5 ${(ceremony.songs || []).length > 0 ? "text-emerald-300" : "text-amber-300"}`}><StepNum n={2} done={(ceremony.songs || []).length > 0} /><Music size={13} /> {t("po.cer_songs_label")}</Label>
                <div className="mt-1 flex gap-2">
                  <Input value={songInput} onChange={(e) => setSongInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSong(); } }} placeholder={t("po.cer_songs_placeholder")} data-testid="ceremony-song-input" />
                  <Button size="sm" className="shrink-0 rounded-full" onClick={addSong} data-testid="ceremony-song-add-btn"><Check size={14} className="mr-1.5" /> {t("po.save")}</Button>
                </div>
                {(ceremony.songs || []).length > 0 && (
                  <ul className="mt-2 space-y-1" data-testid="ceremony-song-list">
                    {ceremony.songs.map((s, i) => (
                      <li key={i} className="flex items-center justify-between rounded-lg border border-sky-400/30 bg-sky-500/15 px-3 py-1.5 text-sm text-sky-50">
                        <span className="flex items-center gap-2 truncate"><Music size={13} className="shrink-0 text-sky-300" /> {s}</span>
                        <button onClick={() => removeSong(i)} className="text-red-300" data-testid={`ceremony-song-remove-${i}`}><X size={13} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Photos de la cérémonie */}
              <div>
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <Label className={`flex items-center gap-1.5 text-xs ${(ceremony.photos || []).length > 0 ? "text-emerald-300" : "text-amber-300"}`}><StepNum n={3} done={(ceremony.photos || []).length > 0} /> {t("po.cer_photos_label")} ({(ceremony.photos || []).length}/{MAX_PHOTOS})</Label>
                  <div className="flex items-center gap-2">
                    {ceremony.want_photos !== false && (
                      <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => cerPhotoRef.current?.click()} disabled={(ceremony.photos || []).length >= MAX_PHOTOS} data-testid="ceremony-photo-add-btn"><ImagePlus size={13} className="mr-1.5" /> {t("po.add")}</Button>
                    )}
                    <WantToggle value={ceremony.want_photos !== false} onChange={(v) => setCerWant("want_photos", v)} testid="ceremony-want-photos" />
                  </div>
                  <input ref={cerPhotoRef} type="file" accept="image/*" multiple className="hidden" onChange={addCeremonyPhotos} />
                </div>
                {ceremony.want_photos === false ? (
                  <p className="rounded-lg border border-dashed border-white/15 px-3 py-2 text-[11px] text-muted-foreground" data-testid="ceremony-photos-off">{t("po.cer_photos_off")}</p>
                ) : (ceremony.photos || []).length > 0 && (
                  <>
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="ceremony-photo-list">
                      {ceremony.photos.map((p, i) => (
                        <div key={i} draggable onDragStart={() => { dragFrom.current = i; }} onDragOver={(e) => e.preventDefault()} onDrop={() => dropCeremony(i)} className="group relative aspect-square cursor-grab overflow-hidden rounded-xl border border-white/10 bg-black active:cursor-grabbing" data-testid={`ceremony-photo-thumb-${i}`}>
                          <img src={p} alt="" className="h-full w-full object-cover" />
                          <span className="absolute left-1 top-1 rounded-full bg-black/70 px-1.5 text-[10px] font-semibold text-white">{i + 1}</span>
                          <button onClick={() => removeCeremonyPhoto(i)} className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-red-300" data-testid={`ceremony-photo-remove-${i}`}><Trash2 size={12} /></button>
                          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between bg-gradient-to-t from-black/75 to-transparent px-1.5 pb-1.5 pt-4 opacity-0 transition-opacity group-hover:opacity-100">
                            <button disabled={i === 0} onClick={() => moveCeremonyPhoto(i, -1)} className="pointer-events-auto rounded-full bg-white/25 p-1 text-white backdrop-blur transition hover:bg-white/45 disabled:opacity-0" data-testid={`ceremony-photo-left-${i}`} title={t("po.title_move_left")}><ChevronLeft size={14} /></button>
                            <button disabled={i === ceremony.photos.length - 1} onClick={() => moveCeremonyPhoto(i, 1)} className="pointer-events-auto rounded-full bg-white/25 p-1 text-white backdrop-blur transition hover:bg-white/45 disabled:opacity-0" data-testid={`ceremony-photo-right-${i}`} title={t("po.title_move_right")}><ChevronRight size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Sparkles size={12} className="text-amber-300" /> {t("po.drag_hint")}</span>
                      <Button size="sm" variant="outline" className="rounded-full border-amber-400/40 text-amber-200" onClick={() => { setCerSlide(0); setShowCerSlideshow(true); }} data-testid="ceremony-slideshow-btn"><Play size={14} className="mr-1.5" /> {t("po.see_slideshow")}</Button>
                    </div>
                  </>
                )}
              </div>

              {/* Musique du diaporama de la cérémonie */}
              <div className="mt-3" data-testid="ceremony-music-wrap">
                <MusicPicker
                  tracks={musicTracks}
                  on={cerMusicOn} choice={cerMusicChoice} ownReady={cerMusicReady} ownName={cerMusicName} ownUrl={cerMusicUrl}
                  busy={cerMusicBusy} progress={cerMusicProgress}
                  onToggle={() => toggleMusicOn("ceremony")} onChoose={(id) => chooseMusic("ceremony", id)}
                  onImport={() => cerMusicInputRef.current?.click()} onRemove={() => removeMusic("ceremony")}
                  onSave={async () => { await chooseMusic("ceremony", cerMusicChoice); toast.success(t("po.music_saved")); }}
                  inputRef={cerMusicInputRef} onFile={(e) => uploadMusic(e, "ceremony")} prefix="ceremony"
                />
              </div>

              {showCerSlideshow && (ceremony.photos || []).length > 0 && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4" data-testid="ceremony-slideshow-modal" onClick={() => setShowCerSlideshow(false)}>
                  {cerMusicOn && (
                    <audio src={resolveMusicSrc(musicTracks, cerMusicChoice, cerMusicReady, cerMusicUrl)} autoPlay onEnded={() => setShowCerSlideshow(false)} data-testid="ceremony-slideshow-audio" />
                  )}
                  <button className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" onClick={() => setShowCerSlideshow(false)} data-testid="ceremony-slideshow-close"><X size={22} /></button>
                  <img key={cerSlide} src={ceremony.photos[cerSlide]} alt="" className="slide-fade max-h-[82vh] max-w-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
                  {ceremony.photos.length > 1 && (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); setCerSlide((s) => (s - 1 + ceremony.photos.length) % ceremony.photos.length); }} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20"><ChevronLeft size={24} /></button>
                      <button onClick={(e) => { e.stopPropagation(); setCerSlide((s) => (s + 1) % ceremony.photos.length); }} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20"><ChevronRight size={24} /></button>
                      <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-1.5">{ceremony.photos.map((_, i) => <span key={i} className={`h-2 w-2 rounded-full ${i === cerSlide ? "bg-amber-300" : "bg-white/40"}`} />)}</div>
                    </>
                  )}
                </div>
              )}

              {/* Vidéo de la cérémonie */}
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={`flex items-center gap-1.5 text-xs ${ceremony.video_ready ? "text-emerald-300" : "text-amber-300"}`}><StepNum n={4} done={!!ceremony.video_ready} tone="sky" /><Video size={14} /> {t("po.cer_video_label")} <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-semibold text-sky-200">{t("po.video_max")}</span></span>
                  <WantToggle value={ceremony.want_video !== false} onChange={(v) => setCerWant("want_video", v)} testid="ceremony-want-video" />
                </div>
                <input ref={cerVideoRef} type="file" accept="video/*" className="hidden" onChange={uploadCeremonyVideo} />
                {ceremony.want_video === false && (
                  <p className="mt-2 rounded-lg border border-dashed border-white/15 px-3 py-2 text-[11px] text-muted-foreground" data-testid="ceremony-video-off">{t("po.cer_video_off")}</p>
                )}

                {ceremony.want_video !== false && !ceremony.video_ready && !camOpen && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" className="rounded-full border-sky-400/40 text-sky-200" onClick={() => cerVideoRef.current?.click()} disabled={cerVideoBusy} data-testid="ceremony-video-upload-btn">{cerVideoBusy ? `${t("po.sending")} ${cerVideoProgress}%` : <><ImagePlus size={13} className="mr-1.5" /> {t("po.import_video")}</>}</Button>
                    <Button size="sm" variant="outline" className="rounded-full border-sky-400/40 text-sky-200" onClick={openCeremonyCamera} disabled={cerVideoBusy} data-testid="ceremony-video-record-btn"><Video size={13} className="mr-1.5" /> {t("po.record_camera")}</Button>
                  </div>
                )}

                {camOpen && (
                  <div className="mt-2" data-testid="ceremony-recording-box">
                    <video ref={recPreviewRef} muted playsInline autoPlay className="aspect-video w-full rounded-lg border border-white/10 bg-black object-cover" />
                    {!recording ? (
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">{t("po.ready_max2")}</span>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="rounded-full border-white/15 text-muted-foreground" onClick={cancelCeremonyCamera} data-testid="ceremony-record-cancel-btn">{t("po.cancel")}</Button>
                          <Button size="sm" className="rounded-full bg-emerald-500 text-black hover:bg-emerald-400" onClick={startRecording} data-testid="ceremony-record-start-btn"><Video size={13} className="mr-1.5" /> {t("po.start")}</Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="mt-2 flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-sm font-semibold text-red-400" data-testid="ceremony-record-countdown"><span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" /> {fmtSec(recLeft)} {t("po.remaining")}</span>
                          <Button size="sm" variant="outline" className="rounded-full border-red-400/50 text-red-300" onClick={stopCeremonyRecording} data-testid="ceremony-record-stop-btn"><Check size={13} className="mr-1.5" /> {t("po.stop")}</Button>
                        </div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-white/10"><div className="h-full bg-red-500 transition-all" style={{ width: `${(recLeft / MAX_VIDEO_SEC) * 100}%` }} /></div>
                      </>
                    )}
                  </div>
                )}

                {cerVideoBusy && !recording && <div className="mt-1 text-[11px] text-sky-300">{t("po.sending_video")} {cerVideoProgress}%</div>}
                {ceremony.want_video !== false && ceremony.video_ready && !camOpen && (
                  <div className="mt-2 rounded-lg border border-emerald-400/20 bg-emerald-500/5 px-3 py-2">
                    {cerVideoUrl && (
                      <video src={cerVideoUrl} controls playsInline preload="metadata" className="mb-2 max-h-52 w-full rounded-lg border border-white/10 bg-black object-contain" data-testid="ceremony-video-thumb" />
                    )}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 text-xs text-emerald-300"><Check size={13} /> {t("po.video_saved")}</span>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" className="rounded-full border-sky-400/40 text-sky-200" onClick={() => navigate(`/ceremonie/${lc.id}`)} data-testid="ceremony-video-view-btn"><Play size={13} className="mr-1.5" /> {t("po.view_my_video")}</Button>
                        {!confirmDelVideo && (
                          <Button size="sm" variant="outline" className="rounded-full border-red-400/50 text-red-300 hover:bg-red-500/10" onClick={() => setConfirmDelVideo(true)} data-testid="ceremony-video-delete-btn"><Trash2 size={13} className="mr-1.5" /> {t("po.delete_video")}</Button>
                        )}
                      </div>
                    </div>
                    {confirmDelVideo && (
                      <div className="mt-2 rounded-lg border border-red-400/30 bg-red-500/10 p-2.5" data-testid="ceremony-video-delete-confirm">
                        <p className="mb-2 text-xs text-red-200">{t("po.del_video_confirm")}</p>
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button size="sm" variant="ghost" className="rounded-full text-muted-foreground" onClick={() => setConfirmDelVideo(false)} data-testid="ceremony-video-delete-cancel">{t("po.cancel")}</Button>
                          <Button size="sm" className="rounded-full bg-red-500 text-white hover:bg-red-400" onClick={() => { setConfirmDelVideo(false); removeCeremonyVideo(); }} data-testid="ceremony-video-delete-confirm-btn"><Trash2 size={13} className="mr-1.5" /> {t("po.yes_delete")}</Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Fichiers (PDF ou autre) de la cérémonie */}
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={`flex items-center gap-1.5 text-xs ${(ceremony.files || []).length > 0 ? "text-emerald-300" : "text-amber-300"}`}><StepNum n={5} done={(ceremony.files || []).length > 0} tone="sky" /><Paperclip size={14} /> {t("po.file_label")} <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-semibold text-sky-200">{t("po.file_max")}</span></span>
                  <div className="flex items-center gap-2">
                    {ceremony.want_file !== false && (
                      <Button size="sm" variant="outline" className="rounded-full border-sky-400/40 text-sky-200" onClick={() => cerDocRef.current?.click()} disabled={cerDocBusy} data-testid="ceremony-file-add-btn">{cerDocBusy ? t("po.sending_short") : <><ImagePlus size={13} className="mr-1.5" /> {t("po.add_file")}</>}</Button>
                    )}
                    <WantToggle value={ceremony.want_file !== false} onChange={(v) => setCerWant("want_file", v)} testid="ceremony-want-file" />
                  </div>
                  <input ref={cerDocRef} type="file" accept={ACCEPT_DOCS} multiple className="hidden" onChange={uploadCeremonyDoc} />
                </div>
                {ceremony.want_file === false ? (
                  <p className="mt-2 rounded-lg border border-dashed border-white/15 px-3 py-2 text-[11px] text-muted-foreground" data-testid="ceremony-file-off">{t("po.file_off")}</p>
                ) : (ceremony.files || []).length > 0 && (
                  <ul className="mt-2 space-y-1.5" data-testid="ceremony-file-list">
                    {ceremony.files.map((f) => (
                      <li key={f.file_id} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2" data-testid={`ceremony-file-item-${f.file_id}`}>
                        <button onClick={() => openCeremonyDoc(f.file_id, f.name)} className="flex min-w-0 items-center gap-2 text-left text-xs text-sky-100 hover:underline" data-testid={`ceremony-file-open-${f.file_id}`}>
                          <FileText size={15} className="shrink-0 text-sky-300" />
                          <span className="truncate">{f.name}</span>
                          <span className="shrink-0 text-[10px] text-muted-foreground">{fmtSize(f.size)}</span>
                        </button>
                        <div className="flex shrink-0 items-center gap-1">
                          <button onClick={() => openCeremonyDoc(f.file_id, f.name)} className="rounded-full p-1.5 text-sky-200 hover:bg-white/10" title={t("po.title_download")}><Download size={14} /></button>
                          <button onClick={() => removeCeremonyDoc(f.file_id)} className="rounded-full p-1.5 text-red-300 hover:bg-white/10" title="Supprimer" data-testid={`ceremony-file-remove-${f.file_id}`}><Trash2 size={14} /></button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>


              <div className="flex flex-wrap justify-center gap-2">
                <Button size="sm" className="rounded-full" onClick={saveCeremonyAll} data-testid="ceremony-save-btn"><Check size={14} className="mr-1.5" /> {t("po.save")}</Button>
                <Button size="sm" variant="outline" className="rounded-full border-sky-400/40 text-sky-200" onClick={() => navigate(`/ceremonie/${lc.id}`)} data-testid="ceremony-view-btn"><Video size={14} className="mr-1.5" /> {t("po.view")}</Button>
              </div>
          </div>
        </section>
        <ValidateStepButton validated={done.ceremonie} onClick={() => validateAndNext("ceremonie")} testid="validate-step-ceremonie" />
        <div className="mt-4 border-t border-white/10 pt-4 text-center" data-testid="living-cgu-footer">
          <p className="text-xs text-white/70">
            {t("po.cgu_footer_1")}{" "}
            <button type="button" onClick={() => navigate("/cgu")} className="font-semibold text-sky-300 underline underline-offset-2 hover:text-sky-200" data-testid="living-cgu-link">{t("po.cgu_link")}</button>.
          </p>
        </div>
        </TabsContent>

        {/* 2. Message personnel */}
        <TabsContent value="message" className="mt-4 rounded-2xl border border-white/20 bg-slate-900/55 px-4 pb-4 pt-4 backdrop-blur-xl shadow-xl" data-testid="acc-message">
        {/* Messages individuels */}
        <section className="mb-2" data-testid="living-messages-section">
          <StepRail steps={[
            { n: 1, label: t("po.msg_step_write"), done: messages.length > 0 },
            { n: 2, label: t("po.msg_step_pv"), done: messages.some((m) => m.photos_count || m.video_ready) },
            { n: 3, label: t("po.msg_step_send"), done: messages.some((m) => m.sent_at) },
          ]} />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sky-300"><Mail size={18} /><span className="text-sm font-medium">{t("po.msg_header")}</span></div>
            <Button size="sm" variant="outline" className="rounded-full border-sky-400/40 text-sky-200 hover:bg-sky-500/10" onClick={openNewMsg} data-testid="msg-new-btn"><Plus size={14} className="mr-1.5" /> {t("po.msg_new")}</Button>
          </div>

          {/* Crédits de messages prépayés */}
          <div className={`mt-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2 ${credits > 0 ? "border-emerald-400/40 bg-emerald-500/10" : "border-amber-400/40 bg-amber-500/10"}`} data-testid="msg-credits-banner">
            <span className={`text-xs font-medium ${credits > 0 ? "text-emerald-200" : "text-amber-200"}`}>
              {credits > 0 ? t("po.credits_some", { n: credits }) : t("po.credits_none")}
            </span>
            <Button size="sm" className="rounded-full bg-sky-500 text-white hover:bg-sky-400" onClick={() => setOrderOpen(true)} data-testid="msg-buy-credits-btn"><ShieldCheck size={13} className="mr-1.5" /> {t("po.credits_buy")}</Button>
          </div>
          <p className="mb-1 mt-1 text-base sm:text-lg font-medium leading-relaxed text-white/90" data-testid="msg-desc">{t("po.msg_desc_1")}<br />{t("po.msg_desc_2")}</p>
          <p className="mb-3 flex items-center gap-1.5 rounded-lg bg-emerald-500/10 px-2.5 py-1.5 text-xs font-medium text-emerald-200" data-testid="msg-unlimited-note"><Sparkles size={13} className="text-emerald-300" /> {t("po.msg_unlimited")}</p>

          {/* Formulaire intégré : se déplie vers le bas, se referme après enregistrement */}
          <div className={`grid transition-[grid-template-rows] duration-500 ease-out ${msgOpen ? "mb-4 grid-rows-[1fr]" : "grid-rows-[0fr]"}`} data-testid="msg-form-panel">
            <div className="overflow-hidden">
              {em && (
                <div className="rounded-2xl border border-white/20 bg-slate-900/55 p-4 backdrop-blur-xl shadow-xl">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sky-200"><Mail size={18} /><span className="text-sm font-medium">{em.id ? t("po.msg_edit") : t("po.msg_create")}</span></span>
                    <button onClick={() => setMsgOpen(false)} className="rounded-full p-1.5 text-muted-foreground hover:bg-white/10 hover:text-white" title={t("po.close")} data-testid="msg-close-btn"><X size={18} /></button>
                  </div>

                  <StepRail steps={[
                    { n: 1, label: t("po.msg_step_word"), done: !!(em.text || "").trim() },
                    { n: 2, label: t("po.msg_step_photos"), done: (em.photos || []).length > 0 },
                    { n: 3, label: t("po.msg_step_video"), done: !!em.video_ready },
                    { n: 4, label: t("po.msg_step_file"), done: (em.files || []).length > 0 },
                  ]} />

                  <div className="space-y-3">
                    {/* Destinataire */}
                    <div>
                      <Label className={`flex items-center gap-1.5 text-xs ${(em.recipient_email || "").trim() ? "text-emerald-300" : "text-amber-300"}`}><Mail size={13} /> {t("po.msg_recipient")}</Label>
                      <div className="mt-1 grid gap-2 sm:grid-cols-2">
                        <Input value={em.recipient_name} onChange={(e) => setEm((p) => ({ ...p, recipient_name: e.target.value }))} placeholder={t("po.msg_recipient_name")} data-testid="msg-recipient-name" />
                        <Input type="email" value={em.recipient_email} onChange={(e) => setEm((p) => ({ ...p, recipient_email: e.target.value }))} placeholder={t("po.msg_recipient_email")} data-testid="msg-recipient-email" />
                      </div>
                    </div>

                    {/* 1. Mot */}
                    <div>
                      <div className="mb-1 flex items-center justify-between">
                        <Label className={`flex items-center gap-2 text-xs ${(em.text || "").trim() ? "text-emerald-300" : "text-amber-300"}`}><StepNum n={1} done={!!(em.text || "").trim()} /> {t("po.msg_your_message")}</Label>
                        <WantToggle value={em.want_text !== false} onChange={(v) => setEm((p) => ({ ...p, want_text: v }))} testid="msg-want-text" />
                      </div>
                      {em.want_text !== false ? (
                        <>
                          <Input className="mt-1" value={em.title} onChange={(e) => setEm((p) => ({ ...p, title: e.target.value }))} placeholder={t("po.msg_title_ph")} data-testid="msg-title" />
                          <Textarea className="mt-2" rows={6} value={em.text} onChange={(e) => setEm((p) => ({ ...p, text: e.target.value }))} placeholder={t("po.msg_text_ph")} data-testid="msg-text" />
                        </>
                      ) : (
                        <p className="rounded-lg border border-dashed border-white/15 px-3 py-2 text-[11px] text-muted-foreground" data-testid="msg-text-off">{t("po.msg_text_off")}</p>
                      )}
                    </div>

                    {/* 2. Photos */}
                    <div>
                      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                        <Label className={`flex items-center gap-1.5 text-xs ${(em.photos || []).length > 0 ? "text-emerald-300" : "text-amber-300"}`}><StepNum n={2} done={(em.photos || []).length > 0} /> {t("po.msg_step_photos")} ({(em.photos || []).length}/{MAX_PHOTOS})</Label>
                        <div className="flex items-center gap-2">
                          {em.want_photos !== false && (
                            <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => msgPhotoRef.current?.click()} disabled={(em.photos || []).length >= MAX_PHOTOS} data-testid="msg-add-photo-btn"><ImagePlus size={13} className="mr-1.5" /> {t("po.add")}</Button>
                          )}
                          <WantToggle value={em.want_photos !== false} onChange={(v) => setEm((p) => ({ ...p, want_photos: v, ...(v ? {} : { music_on: false }) }))} testid="msg-want-photos" />
                        </div>
                        <input ref={msgPhotoRef} type="file" accept="image/*" multiple className="hidden" onChange={addMsgPhotos} />
                      </div>
                      {em.want_photos === false ? (
                        <p className="rounded-lg border border-dashed border-white/15 px-3 py-2 text-[11px] text-muted-foreground" data-testid="msg-photos-off">{t("po.msg_photos_off")}</p>
                      ) : (em.photos || []).length > 0 && (
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                          {em.photos.map((p, i) => (
                            <div key={i} className="relative aspect-square overflow-hidden rounded-xl border border-white/10 bg-black">
                              <img src={p} alt="" className="h-full w-full object-cover" />
                              <button onClick={() => removeMsgPhoto(i)} className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-red-300"><Trash2 size={12} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Musique du diaporama */}
                    {em.want_photos !== false && (
                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <span className={`flex items-center gap-2 text-xs font-medium ${em.music_on ? "text-emerald-300" : "text-amber-300"}`}><Music size={14} /> {t("po.music_title")}</span>
                        <button type="button" onClick={() => setEm((p) => ({ ...p, music_on: !p.music_on }))} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${em.music_on ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-200" : "border-white/20 bg-white/5 text-muted-foreground"}`} data-testid="msg-music-toggle">
                          {em.music_on ? <Volume2 size={13} /> : <VolumeX size={13} />} {em.music_on ? t("po.music_on") : t("po.music_off")}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(musicTracks.length ? musicTracks : MUSIC_LIB).map((mm) => (
                          <button key={mm.id} type="button" onClick={() => setEm((p) => ({ ...p, music_choice: mm.id }))} data-testid={`msg-music-choice-${mm.id}`}
                            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${em.music_choice === mm.id ? "border-emerald-400 bg-emerald-500/20 text-emerald-100" : "border-white/15 bg-white/5 text-white/80 hover:bg-white/10"}`}>
                            {em.music_choice === mm.id ? "✓ " : ""}{musicLabel(t, mm)}
                          </button>
                        ))}
                        {em.music_ready && (
                          <button type="button" onClick={() => setEm((p) => ({ ...p, music_choice: "own" }))} data-testid="msg-music-choice-own"
                            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${em.music_choice === "own" ? "border-emerald-400 bg-emerald-500/20 text-emerald-100" : "border-white/15 bg-white/5 text-white/80 hover:bg-white/10"}`}>
                            {em.music_choice === "own" ? "✓ " : ""}🎵 {t("po.my_music")}
                          </button>
                        )}
                      </div>
                      <audio key={em.music_choice === "own" ? (msgOwnMusicUrl || "own") : em.music_choice} src={em.music_choice === "own" ? (msgOwnMusicUrl || undefined) : resolveMusicSrc(musicTracks, em.music_choice)} controls className="mt-2 h-9 w-full" data-testid="msg-music-preview" />
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" className="rounded-full border-sky-400/40 text-sky-200 hover:bg-sky-500/10" onClick={pickMsgMusic} disabled={msgMusicBusy} data-testid="msg-music-upload-btn">
                          {msgMusicBusy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Music size={13} className="mr-1.5" />}{msgMusicBusy ? `${t("po.sending")} ${msgMusicProgress}%` : (em.music_ready ? t("po.replace_music") : t("po.import_music"))}
                        </Button>
                        {em.music_ready && (
                          <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={removeMsgMusic} data-testid="msg-music-remove-btn"><Trash2 size={13} className="mr-1.5" /> {t("po.delete_short")}</Button>
                        )}
                      </div>
                      {msgMusicBusy && <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full bg-sky-400 transition-all" style={{ width: `${msgMusicProgress}%` }} /></div>}
                      <input ref={msgMusicRef} type="file" accept=".mp3,.m4a,audio/*" className="hidden" onChange={uploadMsgMusic} data-testid="msg-music-input" />
                    </div>
                    )}

                    {/* 3. Vidéo */}
                    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className={`flex items-center gap-1.5 text-xs ${em.video_ready ? "text-emerald-300" : "text-amber-300"}`}><StepNum n={3} done={!!em.video_ready} /><Video size={14} /> {t("po.msg_video_opt")}</span>
                        <div className="flex items-center gap-2">
                          {em.want_video !== false && (em.video_ready ? (
                            <Button size="sm" variant="ghost" className="rounded-full text-destructive" onClick={removeMsgVideo} data-testid="msg-video-remove-btn"><Trash2 size={13} className="mr-1.5" /> {t("po.remove_short")}</Button>
                          ) : !msgCamOpen && (
                            <>
                              <Button size="sm" variant="outline" className="rounded-full border-red-400/50 text-red-200 hover:bg-red-500/10" onClick={openMsgCamera} disabled={msgVideoBusy} data-testid="msg-video-record-btn"><Video size={13} className="mr-1.5" /> {t("po.msg_video_record")}</Button>
                              <Button size="sm" variant="outline" className="rounded-full border-sky-400/40 text-sky-200" onClick={pickMsgVideo} disabled={msgVideoBusy} data-testid="msg-video-upload-btn">{msgVideoBusy ? `${t("po.sending")} ${msgVideoProgress}%` : <><ImagePlus size={13} className="mr-1.5" /> {t("po.import_short")}</>}</Button>
                            </>
                          ))}
                          <WantToggle value={em.want_video !== false} onChange={(v) => setEm((p) => ({ ...p, want_video: v }))} testid="msg-want-video" />
                        </div>
                        <input ref={msgVideoRef} type="file" accept="video/*" className="hidden" onChange={uploadMsgVideo} />
                      </div>

                      {msgCamOpen && (
                        <div className="mt-3 rounded-lg border border-red-400/30 bg-black/40 p-2" data-testid="msg-camera">
                          <video ref={msgPreviewRef} muted playsInline className="w-full rounded-md bg-black" style={{ maxHeight: 260 }} />
                          <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                            {!msgRecording ? (
                              <Button size="sm" className="rounded-full bg-red-500 text-white hover:bg-red-400" onClick={startMsgRecording} data-testid="msg-rec-start-btn"><Video size={14} className="mr-1.5" /> {t("po.rec_start")}</Button>
                            ) : (
                              <Button size="sm" className="rounded-full bg-white text-black hover:bg-white/90" onClick={stopMsgRecording} data-testid="msg-rec-stop-btn"><Check size={14} className="mr-1.5" /> {t("po.rec_stop")} ({fmtSec(msgRecLeft)})</Button>
                            )}
                            <Button size="sm" variant="outline" className="rounded-full border-white/15 text-muted-foreground" onClick={cancelMsgCamera} data-testid="msg-rec-cancel-btn"><X size={14} className="mr-1.5" /> {t("po.cancel")}</Button>
                          </div>
                          {msgRecording && <p className="mt-1 text-center text-[11px] text-red-300">{t("po.recording_note")}</p>}
                        </div>
                      )}
                      {em.want_video === false ? (
                        <p className="mt-2 rounded-lg border border-dashed border-white/15 px-3 py-2 text-[11px] text-muted-foreground" data-testid="msg-video-off">{t("po.msg_video_off")}</p>
                      ) : em.video_ready && (
                        <div className="mt-2">
                          {msgVideoUrl && (
                            <video src={msgVideoUrl} controls playsInline preload="metadata" className="mb-1.5 max-h-52 w-full rounded-lg border border-white/10 bg-black object-contain" data-testid="msg-video-thumb" />
                          )}
                          <div className="flex items-center gap-1.5 text-[11px] text-emerald-300"><Check size={12} /> {t("po.video_saved_short")}</div>
                        </div>
                      )}
                    </div>

                    {/* 4. Fichier (PDF ou autre) */}
                    <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className={`flex items-center gap-1.5 text-xs ${(em.files || []).length > 0 ? "text-emerald-300" : "text-amber-300"}`}><StepNum n={4} done={(em.files || []).length > 0} /><Paperclip size={14} /> {t("po.file_label")} <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-semibold text-sky-200">{t("po.file_max")}</span></span>
                        <div className="flex items-center gap-2">
                          {em.want_file !== false && (
                            <Button size="sm" variant="outline" className="rounded-full border-sky-400/40 text-sky-200" onClick={pickMsgDoc} disabled={msgDocBusy} data-testid="msg-file-add-btn">{msgDocBusy ? t("po.sending_short") : <><ImagePlus size={13} className="mr-1.5" /> {t("po.add_file")}</>}</Button>
                          )}
                          <WantToggle value={em.want_file !== false} onChange={(v) => setEm((p) => ({ ...p, want_file: v }))} testid="msg-want-file" />
                        </div>
                        <input ref={msgDocRef} type="file" accept={ACCEPT_DOCS} multiple className="hidden" onChange={uploadMsgDoc} />
                      </div>
                      {em.want_file === false ? (
                        <p className="mt-2 rounded-lg border border-dashed border-white/15 px-3 py-2 text-[11px] text-muted-foreground" data-testid="msg-file-off">{t("po.file_off")}</p>
                      ) : (em.files || []).length > 0 && (
                        <ul className="mt-2 space-y-1.5" data-testid="msg-file-list">
                          {em.files.map((f) => (
                            <li key={f.file_id} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2" data-testid={`msg-file-item-${f.file_id}`}>
                              <button onClick={() => openMsgDoc(f.file_id, f.name)} className="flex min-w-0 items-center gap-2 text-left text-xs text-sky-100 hover:underline" data-testid={`msg-file-open-${f.file_id}`}>
                                <FileText size={15} className="shrink-0 text-sky-300" />
                                <span className="truncate">{f.name}</span>
                                <span className="shrink-0 text-[10px] text-muted-foreground">{fmtSize(f.size)}</span>
                              </button>
                              <div className="flex shrink-0 items-center gap-1">
                                <button onClick={() => openMsgDoc(f.file_id, f.name)} className={"rounded-full p-1.5 text-sky-200 hover:bg-white/10"} title={t("po.tt_download")}><Download size={14} /></button>
                                <button onClick={() => removeMsgDoc(f.file_id)} className="rounded-full p-1.5 text-red-300 hover:bg-white/10" title="Supprimer" data-testid={`msg-file-remove-${f.file_id}`}><Trash2 size={14} /></button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="flex flex-wrap justify-center gap-2 pt-1">
                      <Button size="sm" variant="outline" className="rounded-full border-amber-400/40 text-amber-200 hover:bg-amber-500/10" onClick={() => { setPvSlide(0); setMsgPreview(true); }} data-testid="msg-preview-btn"><Eye size={14} className="mr-1.5" /> {t("po.preview_watch")}</Button>
                      <Button size="sm" className="rounded-full bg-sky-500 text-black hover:bg-sky-400" onClick={() => saveMsg()} disabled={msgBusy} data-testid="msg-save-btn">{msgBusy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Check size={14} className="mr-1.5" />} {t("po.save_short")}</Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {messages.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 py-6 text-center text-xs text-muted-foreground" data-testid="msg-empty">{t("po.msg_empty")}</div>
          ) : (
            <div className="space-y-2" data-testid="msg-list">
              {messages.map((m) => (
                <div key={m.id} className="rounded-xl border border-white/10 bg-black/20 p-3" data-testid="msg-row">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 truncate text-base font-semibold text-sky-100" data-testid="msg-recipient-title"><Mail size={15} className="shrink-0 text-sky-300" /> {m.recipient_name || m.recipient_email}{m.free && <span className="ml-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-300">{t("po.free_short")}</span>}</div>
                      <div className="truncate text-xs text-muted-foreground">{m.title || t("po.untitled")} · {m.recipient_email}</div>
                      <div className="mt-0.5 text-[11px] text-muted-foreground">{m.photos_count} {m.photos_count > 1 ? t("po.photos_s") : t("po.photo_s")}{m.video_ready ? ` · ${t("po.msg_step_video").toLowerCase()}` : ""}{m.files_count ? ` · ${m.files_count} ${m.files_count > 1 ? t("po.files_s") : t("po.file_s")}` : ""}{m.sent_at ? ` · ${t("po.sent_tag")}` : ""}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button onClick={() => openEditMsg(m)} title={t("po.tt_edit")} className="rounded-full p-1.5 text-sky-200 hover:bg-white/10" data-testid="msg-edit-btn"><Pencil size={14} /></button>
                      {m.access_code && <button onClick={() => copyMsgLink(m.access_code)} title={t("po.tt_copy")} className="rounded-full p-1.5 text-sky-200 hover:bg-white/10" data-testid="msg-copy-link-btn"><Link2 size={14} /></button>}
                      <button onClick={() => sendMsg(m)} title={t("po.tt_send")} className="rounded-full p-1.5 text-emerald-300 hover:bg-white/10" data-testid="msg-send-btn"><Send size={14} /></button>
                      <button onClick={() => deleteMsg(m.id)} title={t("po.tt_delete")} className="rounded-full p-1.5 text-destructive hover:bg-white/10" data-testid="msg-delete-btn"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
        {messages.length === 0 && (
        <>
        <div className="mt-5 flex items-center justify-center gap-2" data-testid="msg-skip-wrap">
          <Switch id="msg-skip-switch" checked={messageSkipped} onCheckedChange={toggleNoMessage} className="h-6 w-11 border border-white/40 shadow-inner data-[state=checked]:bg-black data-[state=unchecked]:bg-black [&>span]:h-5 [&>span]:w-5 [&>span]:bg-white [&>span]:shadow-md" data-testid="msg-skip-switch" />
          <label htmlFor="msg-skip-switch" className="cursor-pointer text-center text-xs font-medium text-white/85">{t("po.msg_skip")}</label>
        </div>
        <p className="mt-1 text-center text-[10px] italic text-white/60">{t("po.msg_skip_hint")}</p>
        </>
        )}
        <ValidateStepButton validated={done.message} onClick={() => validateAndNext("message")} testid="validate-step-message" />
        </TabsContent>
        </Tabs>
      </div>

      {/* Aperçu destinataire */}
      {msgPreview && em && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm" data-testid="msg-preview-overlay" onClick={() => setMsgPreview(false)}>
          {em.music_on && (em.photos || []).length > 0 && (
            <audio src={em.music_choice === "own" ? (msgOwnMusicUrl || undefined) : resolveMusicSrc(musicTracks, em.music_choice)} autoPlay loop data-testid="msg-preview-audio" />
          )}
          <div className="relative max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-3xl border border-white/15 bg-slate-900/90 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button className="absolute right-3 top-3 rounded-full bg-white/10 p-1.5 text-white hover:bg-white/20" onClick={() => setMsgPreview(false)} data-testid="msg-preview-close"><X size={18} /></button>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-400/30 px-3 py-1 text-[11px] text-amber-200"><Eye size={12} /> {t("po.pv_badge")}</div>
            <h3 className="text-xl font-semibold text-foreground">{em.title || t("po.pv_default_title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("po.pv_from")}{em.recipient_name ? ` · ${t("po.pv_for")} ${em.recipient_name}` : ""}</p>

            <div className="mt-4 space-y-4">
              {em.text && <p className="whitespace-pre-wrap rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm leading-relaxed text-foreground/90">{em.text}</p>}
              {(em.photos || []).length > 0 && (
                <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-black">
                  <img key={pvSlide} src={em.photos[pvSlide]} alt="" className="slide-fade max-h-[380px] w-full object-contain" />
                  {em.photos.length > 1 && (
                    <>
                      <button onClick={() => setPvSlide((s) => (s - 1 + em.photos.length) % em.photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1.5 text-white"><ChevronLeft size={18} /></button>
                      <button onClick={() => setPvSlide((s) => (s + 1) % em.photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-1.5 text-white"><ChevronRight size={18} /></button>
                      <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">{em.photos.map((_, i) => <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === pvSlide ? "bg-amber-300" : "bg-white/40"}`} />)}</div>
                    </>
                  )}
                </div>
              )}
              {em.video_ready && (
                msgVideoUrl
                  ? <video src={msgVideoUrl} controls playsInline preload="metadata" className="max-h-[420px] w-full rounded-2xl border border-white/10 bg-black object-contain" data-testid="msg-preview-video" />
                  : <div className="flex items-center gap-2 rounded-2xl border border-sky-400/20 bg-sky-500/5 px-4 py-3 text-sm text-sky-200" data-testid="msg-preview-video-note"><Loader2 size={14} className="animate-spin" /> {t("po.pv_loading_video")}</div>
              )}
              {(em.files || []).length > 0 && (
                <div className="space-y-1.5" data-testid="msg-preview-files">
                  {em.files.map((f) => (
                    <button key={f.file_id} onClick={() => openMsgDoc(f.file_id, f.name)} className="flex w-full items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-left text-sm text-sky-100 hover:bg-white/[0.06]">
                      <FileText size={16} className="shrink-0 text-sky-300" />
                      <span className="truncate">{f.name}</span>
                      <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{fmtSize(f.size)}</span>
                      <Download size={14} className="shrink-0 text-sky-200" />
                    </button>
                  ))}
                </div>
              )}
              {!em.text && (em.photos || []).length === 0 && !em.video_ready && (em.files || []).length === 0 && (
                <p className="rounded-2xl border border-dashed border-white/15 py-6 text-center text-xs text-muted-foreground">{t("po.pv_empty")}</p>
              )}
            </div>
          </div>
        </div>
      )}

      <LivingOrderDialog open={orderOpen} onOpenChange={setOrderOpen} returnPath="/postinity/preparer" alreadyCeremony={prepaidCeremony} onPaid={refreshCredits} />
    </div>
  );
}
